// Shared answer-matching utilities used by StudentPaperTake (practice-mode verify)
// and StudentPaperReview (post-paper review correctness display).

// A literal &nbsp; entity (from stored HTML answers/questions) is a space, not text — decode it (plus
// the numeric forms) so tokens like "&nbsp;6.531" compare as "6.531".
export const decodeHtmlSpaces = (v) => String(v ?? "").replace(/&nbsp;|&#160;|&#xa0;/gi, " ");

// Some authored answers (e.g. inequality signs "<"/">") were stored as literal HTML entities
// ("&lt;"/"&gt;") rather than the raw character. Decode any remaining entities after tag-stripping
// so they don't show up as literal "&lt;" text in plain-text answer boxes (which, unlike
// HtmlWithLatex, don't run content through the browser's HTML parser).
export const decodeHtmlEntities = (v) => {
  const s = String(v ?? "");
  if (!/&(?:#\d+|#x[0-9a-fA-F]+|[a-zA-Z][a-zA-Z0-9]+);/.test(s)) return s;
  const textarea = document.createElement("textarea");
  textarea.innerHTML = s;
  return textarea.value;
};

// Extracted mark schemes carry a stray space between a minus sign and its digits ("-&nbsp;7°C"), so
// the value reads as +7 and a correct "-7" is scored wrong. Re-glue the sign, but only where a value
// STARTS (string start or after a list separator) — never after a digit, or the expression "5 - 3"
// would turn into "5 -3".
export const glueLeadingSign = (v) =>
  String(v ?? "")
    .replace(/(^|[,;|(]\s*)[-−–—]\s+(?=[.\d])/g, "$1-")
    // ...and the same sign written as a unicode dash glued to its digits ("–8°C"): a minus, not a
    // range separator, so it must compare as one — otherwise "-8°C" fails its own mark scheme.
    // Mirrors FuzzyAnswerMatching::normalizeFuzzyAnswer.
    .replace(/(^|[\s,;|(])[−–—](?=[.\d])/g, "$1-");

// Mark schemes come out of the rich-text editor wrapped in markup ("<p>33%</p>"). Every client tier
// compares the normalised strings, and none of them stripped the tags — so a wrapped scheme matched
// NOTHING, including its own answer typed back verbatim. Only touched when a real tag is present, so
// a bare inequality answer ("<", "a < b") is left alone. Block tags become a space (two paragraphs are
// two values), inline ones vanish (3<sup>2</sup> must not split into "3 2").
const HTML_TAG_RE = /<\/?[a-z][a-z0-9]*(?:\s[^>]*)?>/gi;
const HTML_BLOCK_TAG_RE = /<\/?(?:p|div|br|li|ul|ol|tr|td|th|table|tbody|thead|h[1-6])(?:\s[^>]*)?>/gi;
export const stripAnswerHtmlTags = (v) => {
  const s = String(v ?? "");
  if (!/<\/?[a-z][a-z0-9]*(?:\s[^>]*)?>/i.test(s)) return s;
  return s.replace(HTML_BLOCK_TAG_RE, " ").replace(HTML_TAG_RE, "");
};

// A degree sign the PDF pipeline lifted as a superscript character: "72⁰" (U+2070), "72º" (masculine
// ordinal) and "72ᵒ" are all the printed "72°". Left alone, U+2070 is folded into a zero EXPONENT by
// normalizeNumericPowerExpr, so isNumericPowerExpression claimed the mark scheme was a power expression
// and the student's correct "72°" failed it. A zero exponent is never an 11+ answer; a degree is.
// Mirrors FuzzyAnswerMatching::canonicalizeDegreeGlyphs.
export const canonicalizeDegreeGlyphs = (v) => String(v ?? "").replace(/[⁰ºᵒ]/g, "°");

export const normalizeVal = (v) => glueLeadingSign(decodeHtmlSpaces(stripAnswerHtmlTags(canonicalizeDegreeGlyphs(v))).replace(/\s+/g, " ").trim()).toLowerCase().replace(/÷/g, '/');

export const normalizeHyphensAndBrackets = (v) =>
  String(v ?? "").replace(/(\w)-(\w)/g, '$1 $2').replace(/[()[\]{}]/g, ' ').replace(/\s+/g, ' ').trim().toLowerCase();

export const stripAnswerBrackets = (val) => {
  if (!val) return val;
  try {
    const p = JSON.parse(val);
    if (Array.isArray(p)) return p.flat(Infinity).map((v) => String(v ?? '').trim()).filter(Boolean).join(' / ');
  } catch { /* not JSON */ }
  return val;
};

export const stripMarkSchemeOperatorPrefix = (v) =>
  String(v ?? "")
    .trim()
    .replace(/^\(\s*(?:\\[a-z]+|[-+x×*÷/])\s*\)\s*/iu, "")
    .trim();

// Strip trailing prime/apostrophe variants so A′ matches A (coordinate geometry notation).
export const stripPrime = (v) => String(v ?? "").replace(/[′'ʹ']+/g, "").trim();

// am/pm may be written with dots ("a.m", "p.m") — e.g. an authored answer-unit suffix of "a.m" gets
// appended to the typed value as "11.20 a.m". Match both the plain and dotted forms throughout.
const AMPM_RE = "a\\.?\\s*m\\.?|p\\.?\\s*m\\.?";

export const parseClockTime = (value) => {
  let v = decodeHtmlSpaces(String(value ?? "").replace(/<[^>]*>/g, " "))
    .toLowerCase()
    .trim();
  if (!v) return null;

  // Students space out the separator ("4 :08", "12. 55 pm") — allow a single space either side of
  // it, but only one: a wider gap ("12.  55") is not a time a student meant to write. Mirrors
  // ClockTimeAnswerMatcher, so the submission is never collapsed before this test.
  const colon = v.match(/^(\d{1,2}) ?[:.] ?(\d{2})\s*(a\.?\s*m\.?|p\.?\s*m\.?)?$/i);
  if (colon) {
    const hour = parseInt(colon[1], 10);
    const minute = parseInt(colon[2], 10);
    if (minute >= 60 || hour > 23) return null;
    const periodRaw = colon[3];
    let period = null;
    if (periodRaw) {
      period = periodRaw.replace(/\s/g, "").startsWith("a") ? "am" : "pm";
    }
    return { hour, minute, period };
  }

  // An o'clock time written without the minutes ("7 pm"). The meridiem is what makes it a time —
  // a bare "7" stays a number, so it never answers a "7:00 pm" mark scheme.
  const oClock = v.match(/^(\d{1,2})\s*(a\.?\s*m\.?|p\.?\s*m\.?)$/i);
  if (oClock) {
    const hour = parseInt(oClock[1], 10);
    if (hour <= 23) {
      return { hour, minute: 0, period: oClock[2].replace(/\s/g, "").startsWith("a") ? "am" : "pm" };
    }
  }

  // A bare space is NOT a separator: "3 24" is neither 3:24 nor 3.24. Mirrors ClockTimeAnswerMatcher.

  const dotted = v.match(/^(\d{1,2})\.(\d{2})$/);
  if (dotted) {
    const hour = parseInt(dotted[1], 10);
    const minute = parseInt(dotted[2], 10);
    if (minute < 60 && hour <= 23) {
      return { hour, minute, period: null };
    }
  }

  return null;
};

// A railway-timetable mark scheme is four digits with no separator — "0800", "0728". The timetable the
// question is read from prints every time that way, so the leading zero is part of how the answer is
// written: "800" is not an answer off that timetable. A submission carrying the same four digits in
// order ("08:00", "0800 hours") is left to the tiers that decide separator style.
// Mirrors ClockTimeAnswerMatcher::timetableDigitsMissing.
export const timetableDigitsMissing = (submitted, correct, stemAsksForTime = false) => {
  const plain = (v) => decodeHtmlSpaces(String(v ?? "").replace(/<[^>]*>/g, " ")).trim();
  const c = plain(correct).replace(/\s+/g, "");
  // The leading zero is what identifies the scheme as a time on sight. Without it "1200" is just as
  // likely a count, so an afternoon time only qualifies when the stem asked for a time.
  const isTimetable = /^0\d[0-5]\d$/.test(c) || (stemAsksForTime && /^(?:1\d|2[0-3])[0-5]\d$/.test(c));
  if (!isTimetable) return false;
  const s = plain(submitted);
  if (!s) return false;
  return !s.replace(/\D+/g, "").includes(c);
};

const extractWrittenAmPm = (value) => {
  const v = String(value ?? "").toLowerCase();
  if (/(?:^|(?<=\d))\s*a\.?\s*m\.?(?:\b|$)/i.test(v)) return "am";
  if (/(?:^|(?<=\d))\s*p\.?\s*m\.?(?:\b|$)/i.test(v)) return "pm";
  return null;
};

const clockTimePeriodsCompatible = (studentRaw, correctRaw, studentPeriod, correctPeriod) => {
  const studentWritten = extractWrittenAmPm(studentRaw);
  const correctWritten = extractWrittenAmPm(correctRaw);
  const expected = correctWritten ?? correctPeriod;
  if (studentWritten === null) return true;
  if (expected === null) return true;
  return studentWritten === expected;
};

export const clockTimeAnswersEquivalent = (student, correct) => {
  const s = parseClockTime(student);
  const c = parseClockTime(correct);
  if (!s || !c) return false;
  if (s.hour !== c.hour || s.minute !== c.minute) return false;
  return clockTimePeriodsCompatible(student, correct, s.period, c.period);
};

export const normalizeTimeForMatch = (v) => {
  const parsed = parseClockTime(v);
  if (parsed) {
    return `${parsed.hour}:${String(parsed.minute).padStart(2, "0")}`;
  }
  let s = String(v ?? "").replace(/^(\d{1,2})\.(\d{2})\s*(am|pm)?$/i, (_, h, m) => `${h}:${m}`);
  s = s.replace(/\s*(am|pm)$/i, "").trim();
  return s;
};

// A clock-time mark scheme is decided solely by timesMatch (mirrors the backend's
// correctAnswerIsClockTime guard): the fuzzy tiers below collapse whitespace and split "12:55" into a
// {12,55} token set, which would re-accept the wider gaps the one-space limit above rejects. The mark
// scheme is authored HTML so its own whitespace is noise — only the submission is held to one space.
export const correctIsClockTime = (v) =>
  new RegExp(`^\\d{1,2}\\s*[.:]\\s*\\d{2}\\s*(${AMPM_RE})?$`, "i").test(
    decodeHtmlSpaces(v).replace(/\s+/g, " ").trim()
  );

// UK time notation uses a dot ("17.50") as well as a colon ("17:50"), so a dot-separated submission
// with a two-digit minute part is the same time as the colon mark scheme — accept it. A decimal like
// "17.5" has only one fractional digit, so parseClockTime (which requires \d{2} minutes) never
// converts it to a time and it stays incorrect.
// Asymmetric: a bare dotted mark scheme ("12.15" scale reading) must NOT accept a colon ("12:15").
// With am/pm — or a stem that has already asked for a clock time ("At what time does it end?") —
// the field is unambiguously a time, so either separator is fine.
export const timesMatch = (submitted, correct, stemAsksForTime = false) => {
  const sRaw = String(submitted);
  const cRaw = String(correct).replace(/\s+/g, " ");
  const hasAmPm = (v) => new RegExp(`(?:${AMPM_RE})`, "i").test(v);
  // Both sides name a half of the day and they disagree — 7 a.m is not 7:00 pm. Checked before
  // normalizeTimeForMatch, which drops the meridiem and would call them the same time.
  const sPeriod = extractWrittenAmPm(sRaw);
  const cPeriod = extractWrittenAmPm(cRaw);
  if (sPeriod && cPeriod && sPeriod !== cPeriod) return false;
  if (!stemAsksForTime && !hasAmPm(sRaw) && !hasAmPm(cRaw)) {
    const sep = (v) => (/\d\s*:\s*\d/.test(v) ? ":" : /\d\s*\.\s*\d/.test(v) ? "." : null);
    if (sep(cRaw) === "." && sep(sRaw) === ":") return false;
  }
  return normalizeTimeForMatch(sRaw) === normalizeTimeForMatch(cRaw)
    || clockTimeAnswersEquivalent(submitted, correct);
};

// A stem that asks the student to read or state a CLOCK TIME — frees "11:05" to match a dotted
// "11.05" mark scheme. "How long / how much time" asks for a duration and is excluded.
// Mirrors FuzzyAnswerMatching::questionAsksForClockTime.
export const questionAsksForClockTime = (questionText) => {
  const text = plainAnswerText(questionText).toLowerCase();
  if (!text) return false;
  if (/\bhow\s+(?:long|much\s+time)\b/.test(text)) return false;
  return /(?:at|by)?\s*what\s+time\b/.test(text)
    || /\btime\s+(?:does|do|did|will|would|is|are|was|were|should)\b/.test(text)
    || /\b(?:start|starting|finish|finishing|end|ending|arrival|departure)\s+time\b/.test(text)
    || /\bo'?\s?clock\b/.test(text);
};

export const normalizeVulgarFractions = (v) => {
  const map = {
    '½': ' 1/2', '⅓': ' 1/3', '⅔': ' 2/3',
    '¼': ' 1/4', '¾': ' 3/4',
    '⅕': ' 1/5', '⅖': ' 2/5', '⅗': ' 3/5', '⅘': ' 4/5',
    '⅙': ' 1/6', '⅚': ' 5/6',
    '⅐': ' 1/7', '⅛': ' 1/8', '⅜': ' 3/8', '⅝': ' 5/8', '⅞': ' 7/8',
    '⅑': ' 1/9', '⅒': ' 1/10',
  };
  return String(v ?? '').replace(/\u2044/g, '/').replace(/[½⅓⅔¼¾⅕⅖⅗⅘⅙⅚⅐⅛⅜⅝⅞⅑⅒]/g, (ch) => map[ch] ?? ch).trim();
};

export const normalizeLatexFraction = (v) => {
  let s = normalizeVulgarFractions(v);
  s = s.replace(/^\$+|\$+$/g, "").trim();
  // Admins author maths as \(\frac{21}{4}\); drop the delimiters so a LaTeX-formatted mark
  // scheme still compares against a student's plain "21/4".
  s = s.replace(/^\\[([]\s*|\s*\\[)\]]$/g, "").trim();
  s = s.replace(/(\d)\\frac\{([^}]+)\}\{([^}]+)\}/g, "$1 $2/$3");
  s = s.replace(/\\frac\{([^}]+)\}\{([^}]+)\}/g, "$1/$2");
  return s;
};

// The degree branch has no \b (° is not a word character), so "-7°C" / "-7 °c" / "45°" strip to the
// bare number — the backend's normalizeFuzzyAnswer already did this, and the drift made the client
// override a correct server verdict on temperature questions.
// Admin-added units (AnswerUnitVocabulary) arrive at runtime from the settings fetch. A unit that
// earns a chip but is unknown here makes the client override disagree with the server verdict.
let customAnswerUnits = [];
const escapeUnit = (s) => String(s).replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

export const setCustomAnswerUnits = (units) => {
  customAnswerUnits = Array.isArray(units)
    ? [...units].filter((u) => u && String(u).trim()).sort((a, b) => String(b).length - String(a).length)
    : [];
};

export const stripCommonUnit = (v) => {
  const base = "kg|g|mg|cm|mm|km\\/h|m\\/s|kmph|kph|mph|mps|km|m|ml|l|litres?|liters?|metres?|meters?"
    + "|feet|foot|ft|inch(?:es)?|yards?|yds?|miles?|pints?|gallons?|stones?|tonnes?|ounces?|oz|pounds?|lbs?"
    + "|hours?|hrs?|minutes?|mins?|seconds?|secs?"
    + "|degrees?\\s*(?:celsius|fahrenheit)?|celsius|fahrenheit|kilowatts?|watts?|kW|W|RF|calories?|cals?|cal";
  const extra = customAnswerUnits.length ? "|" + customAnswerUnits.map(escapeUnit).join("|") : "";
  const re = new RegExp(`\\s*(?:°\\s*[cf]?|\\b(?:${base}${extra})\\b)\\s*(?:\\^?\\s*[²³23])?\\s*$`, "i");
  return v.replace(re, "").trim();
};

// A fill-blank whose whole answer IS a unit noun ("Andy is 183 ___ tall" -> "centimetres"). The
// unit-stripping tiers only fire when a NUMBER is attached, so a bare "cm" against a mark scheme
// of "centimetres" reduced both sides to "" and never matched. Mirrors
// backend App\Support\AnswerUnitVocabulary::UNIT_NOUN_SYNONYMS — keep both in sync.
const UNIT_NOUN_SYNONYMS = [
  ["cm", "centimetre", "centimetres", "centimeter", "centimeters"],
  ["mm", "millimetre", "millimetres", "millimeter", "millimeters"],
  ["km", "kilometre", "kilometres", "kilometer", "kilometers"],
  ["m", "metre", "metres", "meter", "meters"],
  ["kg", "kilogram", "kilograms"],
  ["g", "gram", "grams", "gramme", "grammes"],
  ["mg", "milligram", "milligrams"],
  ["l", "litre", "litres", "liter", "liters"],
  ["ml", "millilitre", "millilitres", "milliliter", "milliliters"],
  ["cl", "centilitre", "centilitres"],
  ["ft", "foot", "feet"],
  ["in", "inch", "inches"],
  ["yd", "yard", "yards"],
  ["mi", "mile", "miles"],
  ["oz", "ounce", "ounces"],
  ["lb", "pound", "pounds"],
  ["t", "tonne", "tonnes", "ton", "tons"],
  ["hr", "hrs", "hour", "hours"],
  ["min", "mins", "minute", "minutes"],
  ["sec", "secs", "second", "seconds"],
  ["°c", "degree celsius", "degrees celsius", "celsius"],
  ["°f", "degree fahrenheit", "degrees fahrenheit", "fahrenheit"],
];

const unitNounEquivalent = (a, b) => {
  const norm = (v) => String(v ?? "").replace(/<[^>]*>/g, "").replace(/\s+/g, " ").trim().replace(/\.+$/, "").toLowerCase();
  const na = norm(a);
  const nb = norm(b);
  if (!na || !nb) return false;
  if (na === nb) return true;
  return UNIT_NOUN_SYNONYMS.some((group) => group.includes(na) && group.includes(nb));
};

// When the UI already shows an answer_unit chip (RF, W, cals, £…), the student must type only the
// numeric/maths value. These aliases catch "70RF", "70 RF", "900 watts" against a "W" chip, etc.
const UNIT_CHIP_ALIASES = {
  rf: ["rf"],
  w: ["w", "watt", "watts"],
  watt: ["w", "watt", "watts"],
  watts: ["w", "watt", "watts"],
  kw: ["kw", "kilowatt", "kilowatts"],
  kilowatt: ["kw", "kilowatt", "kilowatts"],
  kilowatts: ["kw", "kilowatt", "kilowatts"],
  cal: ["cal", "cals", "calorie", "calories"],
  cals: ["cal", "cals", "calorie", "calories"],
  calorie: ["cal", "cals", "calorie", "calories"],
  calories: ["cal", "cals", "calorie", "calories"],
  "£": ["£"],
  $: ["$"],
  "€": ["€"],
  "¥": ["¥"],
  "%": ["%", "percent", "percentage"],
  p: ["p", "pence"],
  "a.m": ["a.m", "a.m.", "am"],
  "p.m": ["p.m", "p.m.", "pm"],
};

const escapeRegExp = (s) => String(s).replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

export const unitAliasesForChip = (unit) => {
  const raw = String(unit ?? "").trim();
  if (!raw) return [];
  const key = raw.toLowerCase().replace(/\s+/g, "");
  const listed = UNIT_CHIP_ALIASES[key];
  if (listed) return listed;
  return [raw];
};

/**
 * Strip a given answer-unit chip from student typing ("70RF" / "70 RF" → "70" when unit is RF).
 * Also drops leftover letters so the unit cannot be re-typed beside the chip.
 */
export const sanitizeTypedAnswerWithGivenUnit = (raw, unit, { isPrefix = false } = {}) => {
  if (!unit) return String(raw ?? "");
  let v = String(raw ?? "");
  const aliases = [...unitAliasesForChip(unit)].sort((a, b) => b.length - a.length);
  for (let pass = 0; pass < 4; pass += 1) {
    let changed = false;
    for (const alias of aliases) {
      const esc = escapeRegExp(alias);
      const re = isPrefix
        ? new RegExp(`^\\s*${esc}\\s*`, "i")
        : new RegExp(`\\s*${esc}\\s*$`, "i");
      const next = v.replace(re, "");
      if (next !== v) {
        v = next;
        changed = true;
        break;
      }
    }
    if (!changed) break;
  }
  // Unit chip already supplies the unit — keep numeric / maths tokens only (fractions, decimals, °).
  // The Math Tools minus button inserts U+2212, so it must survive here — dropping it silently
  // deleted the sign from a negative answer ("−8" in a °C field) as the student typed it.
  v = v.replace(/[^0-9\s.,/:\-−–—+()×÷°²³⁴½⅓⅔¼¾⅕⅖⅗⅘⅙⅚⅛⅜⅝⅞]/g, "");
  return v;
};

// When answer_data.answer_input_mode is "numeric" the mark scheme cannot be answered with words
// (QuestionBankAnswer::markSchemeIsNumericOnly), so the typed box drops letters as they are typed —
// "25/12a" can never reach grading. Only Latin letters go: π, √, °, ², ½ and the rest of the Math
// Tools palette are maths notation, not words.
export const isNumericOnlyAnswerMode = (answerData) =>
  String(answerData?.answer_input_mode ?? "") === "numeric";

export const stripLettersFromNumericAnswer = (raw) =>
  String(raw ?? "").replace(/[A-Za-z]/g, "");

// A single number written with a space where its decimal point belongs — "5 04" for "£5.04", "3 23"
// for "3.24". Invalid notation, not a typo to repair: reading the space as the point turned a wrong
// value into a right one. Only 1–2 digits after the space (three is a thousands group, "9 625" =
// 9625) and only against a single-value numeric mark scheme — against a comma-joined list "12 8"
// legitimately answers "12, 8". Mirrors FuzzyAnswerMatching::submittedSpacesDecimalPoint.
export const submittedSpacesDecimalPoint = (submitted, correct) => {
  const plain = (v) =>
    stripCommonUnit(
      decodeHtmlSpaces(String(v ?? "")).replace(/<[^>]*>/g, "").replace(/\s+/g, " ").trim().replace(/^[£$€¥]\s*/, "")
    )
      .replace(/\s*%$/, "")
      .trim();
  if (!/^-?\d+(?:\.\d+)?$/.test(plain(correct))) return false;
  return /^-?\d+ \d{1,2}$/.test(plain(submitted));
};

// A plain number with no unit — "18", "18.5", "-3", "3/4", "1 1/2", "1,200", "1 000". Used to enforce:
// when the mark scheme is JUST a number, the student answer must be too (no appended unit/word). The
// value carries no "cm"/"m"/"kg" etc. — anything with a trailing unit is NOT a plain number.
export const isPlainNumberVal = (v) => {
  // A LaTeX-authored mark scheme ("5 \frac{23}{24}") must be recognised as a plain mixed number
  // too, or this check silently no-ops for it and the "submission must also be a plain number"
  // guard elsewhere never fires.
  let s = normalizeLatexFraction(String(v ?? "")).trim().toLowerCase();
  if (s === "") return false;
  return /^[-+]?\d{1,3}(?:[,\s]\d{3})+(?:\.\d+)?$/.test(s)   // grouped thousands "1,200" / "1 000"
    || /^[-+]?\d+(?:\.\d+)?$/.test(s)                         // integer / decimal
    || /^[-+]?\d+\s*\/\s*\d+$/.test(s)                        // fraction "3/4"
    || /^[-+]?\d+\s+\d+\s*\/\s*\d+$/.test(s);                 // mixed number "1 1/2"
};

const answerTokensForMatch = (v) =>
  normalizeVal(stripMarkSchemeOperatorPrefix(v)).split(" ").filter(Boolean);

export const questionAsksForCount = (questionText) => {
  const q = decodeHtmlSpaces(String(questionText ?? ""))
    .replace(/<[^>]*>/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .toLowerCase();
  return /\b(?:how many|number of)\b/.test(q);
};

const countSuffixAppearsInQuestion = (suffix, questionText) => {
  const stem = String(suffix ?? "").toLowerCase().replace(/[^a-z]/g, "");
  if (stem.length < 2) return false;
  const q = decodeHtmlSpaces(String(questionText ?? ""))
    .replace(/<[^>]*>/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .toLowerCase();
  const base = stem.replace(/s$/, "");
  // Mark-scheme abbreviations ("cals", "W") must still match the stem wording ("calories", "watts").
  const aliases = {
    cal: ["cal", "cals", "calorie", "calories"],
    watt: ["watt", "watts", "w"],
    w: ["watt", "watts", "w"],
    kw: ["kw", "kilowatt", "kilowatts"],
    rf: ["rf"],
  };
  const forms = aliases[base] || [base, `${base}s`];
  return forms.some((form) => new RegExp(`\\b${form.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}\\b`, "i").test(q));
};

export const isOrderedTokenPrefix = (submitted, correct) => {
  const short = answerTokensForMatch(submitted);
  const long = answerTokensForMatch(correct);
  if (short.length === 0 || long.length === 0 || short.length >= long.length) return false;
  return short.every((token, index) => token === long[index]);
};

/** "How many edges?" — bare "12" is fully correct vs mark scheme "12 edges". */
export const countQuestionNumericAnswerEquivalent = (submitted, correct, questionText) => {
  if (!questionAsksForCount(questionText) || !isOrderedTokenPrefix(submitted, correct)) return false;
  const short = answerTokensForMatch(submitted);
  const long = answerTokensForMatch(correct);
  if (long.length !== short.length + 1) return false;
  const suffix = long[long.length - 1];
  if (!countSuffixAppearsInQuestion(suffix, questionText)) return false;
  if (!short.every((token) => isPlainNumberVal(token))) return false;
  return short.every((token, index) => token === long[index]);
};

export const parseValueWithUnit = (v) => {
  const s = String(v ?? "").trim().toLowerCase();
  const poundMatch = s.match(/^£\s*(\d+(?:\.\d+)?)\s*$/);
  if (poundMatch) return [parseFloat(poundMatch[1]) * 100, "p", true];
  // "124 m2" / "124 square metres" are the spellings a student without a superscript key can type —
  // they name the same unit as the mark scheme's "124 m²" and must parse to it.
  const squared = s.match(/^(-?\d+(?:\.\d+)?)\s*(?:(mm|cm|m|km)\s*[²³23]|square\s+(millimetres?|millimeters?|centimetres?|centimeters?|metres?|meters?|kilometres?|kilometers?))\s*$/i);
  if (squared) {
    const word = { millimetre: "mm", millimeter: "mm", centimetre: "cm", centimeter: "cm", metre: "m", meter: "m", kilometre: "km", kilometer: "km" };
    const base = squared[2] ?? word[String(squared[3] ?? "").replace(/s$/, "")];
    const cubic = /[³3]\s*$/.test(s);
    if (base) return [parseFloat(squared[1]), `${base}${cubic ? "³" : "²"}`];
  }
  const unitMatch = s.match(/^(-?\d+(?:\.\d+)?)\s*(km²|cm²|m²|km\/h|m\/s|seconds?|minutes?|hours?|days?|secs?|mins?|hrs?|kilowatts?|watts?|calories?|cals?|cal|kW|mm|cm|km|mg|kg|ml|RF|m|g|l|p|W)\s*$/i);
  if (unitMatch) return [parseFloat(unitMatch[1]), unitMatch[2]];
  if (/^-?\d+(?:\.\d+)?\s*$/.test(s)) return [parseFloat(s), ""];
  // Stray trailing "o"/"O" typo on a number whose unit is supplied by the field's unit
  // suffix (e.g. "1o" typed in a metres field) — read it as the leading number.
  const strayO = s.match(/^(-?\d+(?:\.\d+)?)o+$/);
  if (strayO) return [parseFloat(strayO[1]), ""];
  return null;
};

export const UNIT_CONVERSIONS = {
  mm: [1, "mm", "length"], cm: [10, "mm", "length"], m: [1000, "mm", "length"], km: [1000000, "mm", "length"],
  "cm²": [100, "mm²", "area"], "m²": [1000000, "mm²", "area"], "km²": [1e12, "mm²", "area"],
  "cm³": [1000, "mm³", "volume_solid"], "m³": [1e9, "mm³", "volume_solid"], "km³": [1e18, "mm³", "volume_solid"],
  mg: [1, "mg", "weight"], g: [1000, "mg", "weight"], kg: [1000000, "mg", "weight"],
  ml: [1, "ml", "volume"], l: [1000, "ml", "volume"],
  p: [1, "p", "currency"],
  s: [1, "s", "time"], sec: [1, "s", "time"], secs: [1, "s", "time"],
  second: [1, "s", "time"], seconds: [1, "s", "time"],
  min: [60, "s", "time"], mins: [60, "s", "time"], minute: [60, "s", "time"], minutes: [60, "s", "time"],
  hr: [3600, "s", "time"], hrs: [3600, "s", "time"], hour: [3600, "s", "time"], hours: [3600, "s", "time"],
  day: [86400, "s", "time"], days: [86400, "s", "time"],
};

/**
 * Parse compound values like "1 hour 30 min", "1 m 50 cm", "2 kg 300 g".
 * Returns [canonicalValue, canonUnit, dimension] or null.
 */
export const parseCompoundValue = (v) => {
  const s = String(v ?? "").trim().toLowerCase();
  if (!s) return null;
  const pattern = /(-?\d+(?:\.\d+)?)\s*(km²|cm²|m²|seconds?|minutes?|hours?|days?|secs?|mins?|hrs?|mm|cm|km|mg|kg|ml|m|g|l|p)/g;
  const matches = [...s.matchAll(pattern)];
  if (matches.length < 2) return null;
  let total = 0;
  let canonUnit = null;
  let canonDim = null;
  for (const m of matches) {
    const num = parseFloat(m[1]);
    const unit = m[2];
    const conv = UNIT_CONVERSIONS[unit];
    if (!conv) return null;
    const [factor, cu, cd] = conv;
    if (canonDim === null) { canonDim = cd; canonUnit = cu; }
    else if (cd !== canonDim) return null;
    total += num * factor;
  }
  return canonDim !== null ? [total, canonUnit, canonDim] : null;
};

// "State the units of your answer" — the stem test ExtractsAnswerUnit uses to drop the unit chip.
const questionAsksToStateUnits = (questionText) => {
  const plain = plainAnswerText(questionText ?? "").toLowerCase().replace(/\s+/g, " ");
  return /\b(?:state|give|include|write|show)\b[^.]{0,40}\bunits?\b/.test(plain)
    || /\bunits?\s+of\s+your\s+answer\b/.test(plain);
};

// Decides a measured answer, or returns null to leave it to the tiers below:
//  • the stem asks the student to state the units → the unit must be there, and be equivalent;
//  • the mark scheme is an area / volume → a unit the student does name must be that dimension
//    ("30 cm" is a length, not "30 cm²"). A bare number is left alone — those boxes carry a unit chip.
const measuredUnitVerdict = (submitted, correct, questionText) => {
  const plainCorrect = plainAnswerText(correct).trim();
  const plainSubmitted = plainAnswerText(submitted).trim();
  const cParsed = parseValueWithUnit(plainCorrect);
  if (!cParsed || !cParsed[1]) return null;
  const sParsed = parseValueWithUnit(plainSubmitted);

  if (questionAsksToStateUnits(questionText)) {
    if (!sParsed || !sParsed[1]) return false;
    return isUnitEquivalent(plainSubmitted, plainCorrect);
  }
  if (/[²³]$/.test(cParsed[1]) && sParsed && sParsed[1] && !isUnitEquivalent(plainSubmitted, plainCorrect)) {
    return false;
  }
  return null;
};

export const isUnitEquivalent = (submitted, correct) => {
  const sp = parseValueWithUnit(submitted);
  const cp = parseValueWithUnit(correct);

  if (sp && cp) {
    const [sNum, sUnit] = sp;
    const [cNum, cUnit] = cp;
    if (sUnit && cUnit) {
      const sc = UNIT_CONVERSIONS[sUnit];
      const cc = UNIT_CONVERSIONS[cUnit];
      if (sc && cc && sc[2] === cc[2]) return Math.abs(sNum * sc[0] - cNum * cc[0]) < 0.001;
      return false;
    }
    if (!sUnit && cUnit) {
      // A bare student number matches the mark-scheme NUMBER only (value-first) — it must NOT
      // be silently reinterpreted as the parent unit. That made "51" match "51000 m" (reading 51
      // as 51 km) and "1.75" match "175p" (reading 1.75 as £1.75). Mirrors the backend's
      // MeasurementUnitAnswerMatcher, which never does this parent-unit upgrade either.
      if (cp[2]) return false;
      return Math.abs(sNum - cNum) < 0.001;
    }
    if (sUnit && !cUnit) {
      if (sp[2]) return false;
      return Math.abs(sNum - cNum) < 0.001;
    }
  }

  // Compound value check: "1 hour 30 min" vs "90 min", "1 m 50 cm" vs "150 cm", etc.
  const sCompound = parseCompoundValue(submitted);
  const cCompound = parseCompoundValue(correct);
  if (sCompound !== null || cCompound !== null) {
    const resolveCanon = (compound, parsed) => {
      if (compound) return compound;
      if (parsed && parsed[1]) {
        const conv = UNIT_CONVERSIONS[parsed[1]];
        if (conv) return [parsed[0] * conv[0], conv[1], conv[2]];
      }
      return null;
    };
    const sCanon = resolveCanon(sCompound, sp);
    const cCanon = resolveCanon(cCompound, cp);
    if (sCanon && cCanon && sCanon[2] === cCanon[2]) {
      return Math.abs(sCanon[0] - cCanon[0]) < 0.001;
    }
  }

  return false;
};

/** Fractional digit count on a bare number ("4.71" → 2, "4.7" → 1, "5" → 0). Null if not numeric. */
export const decimalPlaceCount = (value) => {
  const bare = stripCommonUnit(String(value ?? "").trim().toLowerCase().replace(/[£$€¥%]/g, "")).trim();
  if (!/^-?\d+(?:\.\d+)?$/.test(bare)) return null;
  const dot = bare.indexOf(".");
  return dot === -1 ? 0 : bare.length - dot - 1;
};

/**
 * A decimal mark scheme only tolerates extra decimal places when it LOOKS like a rounded
 * non-terminating value ("4.7" for 14/3, "4.33" for 13/3). An exact terminator ("4.5", "0.25",
 * "12.5") IS the whole answer, so a longer value that merely rounds back to it ("4.54", "0.254")
 * is a different quantity and must stay wrong. Mirrors the backend's schemeIsRoundedNonTerminating.
 */
export const schemeIsRoundedNonTerminating = (correct) => {
  const bare = stripCommonUnit(String(correct ?? "").trim().toLowerCase().replace(/[£$€¥%]/g, "")).trim();
  const m = bare.match(/^-?\d+\.(\d+)$/);
  if (!m) return false;
  const frac = m[1];
  // A single fractional digit says nothing ("5.4" is exactly 54/10 as often as it is a rounded 5.44…),
  // so only a repeated final digit ("4.33", "0.6667") counts as a rounded recurring value.
  return frac.length >= 2 && frac[frac.length - 1] === frac[frac.length - 2];
};

/**
 * A recurring decimal written to a different number of places than the mark scheme: the submission
 * either continues the scheme's repeating digit ("4.3333" for "4.3", "0.3333" for "0.333") or rounds
 * the repeating expansion to fewer places ("0.33" for "0.333"). Outside a percentage a 1-d.p. scheme
 * needs a real repeating tail — one appended digit ("5.44" for "5.4 m") is a different answer.
 * Mirrors FuzzyAnswerMatching::percentOrRecurringDecimalMatch (the recurring half; the take page
 * decides these itself whenever the server returns no rule verdict).
 */
export const recurringDecimalExtensionMatch = (submitted, correct) => {
  const clean = (v) =>
    String(v ?? "")
      .replace(/<[^>]*>/g, "")
      .replace(/\bper\s*cent(?:age)?\b/gi, "%")
      .replace(/[%\s ]/g, "")
      .trim();
  const c = clean(correct);
  const s = clean(submitted);
  if (!/^-?\d+\.\d+$/.test(c) || !/^-?\d+\.\d+$/.test(s)) return false;
  const cFrac = c.slice(c.indexOf(".") + 1);
  const sFrac = s.slice(s.indexOf(".") + 1);
  const last = cFrac[cFrac.length - 1];
  const recurringLooking = cFrac.length >= 2
    ? cFrac[cFrac.length - 1] === cFrac[cFrac.length - 2]
    : last !== "0" && last !== "5";
  if (!recurringLooking) return false;
  const hasPercent = String(correct).includes("%") || String(submitted).includes("%");

  if (sFrac.length > cFrac.length) {
    if (!s.startsWith(c)) return false;
    const extra = sFrac.slice(cFrac.length);
    if (cFrac.length === 1 && !hasPercent && extra.length < 2) return false;
    return extra !== "" && extra.split("").every((d) => d === last);
  }
  if (sFrac.length < cFrac.length) {
    const extended = Number(`${c.slice(0, c.indexOf("."))}.${cFrac}${last.repeat(12)}`);
    return extended.toFixed(sFrac.length) === s;
  }
  return false;
};

/**
 * Mark scheme to N d.p. ("4.7 mm") — a more precise working value that rounds to it ("4.71") is accepted.
 * Integer schemes stay strict ("5.1" ≠ "5"), and so do exact terminators ("4.54" ≠ "4.5").
 */
export const roundedDecimalPrecisionMatch = (submitted, correct) => {
  const cDp = decimalPlaceCount(correct);
  const sDp = decimalPlaceCount(submitted);
  if (cDp === null || sDp === null || cDp < 1 || sDp <= cDp) return false;
  if (!schemeIsRoundedNonTerminating(correct)) return false;
  const sn = plainNumericValue(stripCommonUnit(String(submitted ?? "").trim()));
  const cn = plainNumericValue(stripCommonUnit(String(correct ?? "").trim()));
  if (sn === null || cn === null) return false;
  const factor = 10 ** cDp;
  return Math.abs(Math.round(sn * factor) / factor - cn) < 1e-9;
};

export const questionAsksForEstimate = (questionText) => {
  const q = decodeHtmlSpaces(String(questionText ?? ""))
    .replace(/<[^>]*>/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .toLowerCase();
  if (!q) return false;
  return /\bestimate\b/.test(q) || /\bapproximately\b/.test(q) || /\broughly\b/.test(q);
};

/**
 * Estimate / approximately stems accept a precise calculation that rounds to an integer
 * mark-scheme estimate (e.g. Bertie bee 34.14 for 34). A non-integer scheme ("3.24") stays
 * exact — nearby values like 3.23 / "3 23" are incorrect.
 */
export const estimateNumericAnswerEquivalent = (submitted, correct, questionText) => {
  if (!questionAsksForEstimate(questionText)) return false;
  const bareSubmitted = stripCommonUnit(String(submitted ?? "").trim());
  const bareCorrect = stripCommonUnit(String(correct ?? "").trim());
  if (!isPlainNumberVal(bareSubmitted) || !isPlainNumberVal(bareCorrect)) return false;
  const s = plainNumericValue(bareSubmitted);
  const c = plainNumericValue(bareCorrect);
  if (s === null || c === null) return false;
  if (Math.abs(c - Math.round(c)) >= 0.0001) return false;
  if (Math.round(s) === Math.round(c)) return true;
  return Math.abs(s - c) <= 0.5;
};

// Split on newlines, semicolons, space-pipe-space (" | ", the frontend's multi-fill
// joiner), and commas — but NOT thousands-separator commas (digit,digit{3}non-digit).
// "B and D" is normalised to "B, D" before splitting.
const expandMultiValueAndJoiners = (v) => {
  let out = String(v ?? "").trim();
  if (!out) return out;
  let prev = "";
  while (prev !== out) {
    prev = out;
    out = out.replace(/\b([A-Za-z])\s+and\s+([A-Za-z])\b/gi, "$1, $2");
    out = out.replace(/\b(\d+(?:\.\d+)?)\s+and\s+(\d+(?:\.\d+)?)\b/g, "$1, $2");
  }
  return out;
};

export const splitMultiValueAnswer = (v) =>
  expandMultiValueAndJoiners(decodeHtmlSpaces(String(v ?? "")).replace(/<[^>]*>/g, " "))
    .split(/[\n\r;]|\s+\|\s+|,(?!\d{3}(?!\d))/)
    .map((p) => p.trim())
    .filter(Boolean);

// A single number mistyped with a comma decimal separator ("3,00", "£3,5", "0,918", "3,0 m") — one
// comma between digits, ignoring a leading currency symbol and a trailing unit. Not a list
// ("3, 5, 7") or thousands group ("1,200").
export const isSingleMalformedNumber = (v) => {
  let s = decodeHtmlSpaces(String(v ?? "")).replace(/<[^>]*>/g, "").trim().replace(/^[£$€¥]/, "").trim();
  s = s.replace(/\s*(?:mm|cm|km|kg|mg|ml|m|g|l|p|cm²|cm2|m²|m2|km²|km2|°c|°f|°)\s*$/iu, "").trim();
  if (/^-?[1-9]\d{0,2}(?:,\d{3})+(?:\.\d+)?$/.test(s)) return false;
  return /^-?\d+,\d+$/.test(s);
};

// "0,918" for 0.918 — the comma is not a decimal point. Only against a single-value numeric mark
// scheme: a comma-joined list scheme ("12, 7") is legitimately answered "12,7". Mirrors the
// submittedIsSingleMalformedNumber arm of FuzzyAnswerMatching::submissionIsDeterministicallyWrong,
// which the client was missing — so the AI panel could still overturn the rule verdict and show
// "Correct" on the Result / Review pages over a stored 0 marks.
// Also "10,274" for "10.274": looks like thousands grouping to isSingleMalformedNumber, but the
// digit groups match the mark scheme's decimal after swapping the separator.
export const submittedCommaDecimalsNumber = (submitted, correct) => {
  const c = stripCommonUnit(
    decodeHtmlSpaces(String(correct ?? "")).replace(/<[^>]*>/g, "").replace(/\s+/g, " ").trim().replace(/^[£$€¥]\s*/, "")
  ).replace(/\s*%$/, "").trim();
  if (!/^-?\d+(?:\.\d+)?$/.test(c)) return false;
  if (isSingleMalformedNumber(submitted)) return true;
  if (!/^-?\d+\.\d+$/.test(c)) return false;
  let s = decodeHtmlSpaces(String(submitted ?? "")).replace(/<[^>]*>/g, "").trim().replace(/^[£$€¥]/, "").trim();
  s = s.replace(/\s*(?:mm|cm|km|kg|mg|ml|m|g|l|p|cm²|cm2|m²|m2|km²|km2|°c|°f|°)\s*$/iu, "").trim();
  if (!/^-?\d+,\d+$/.test(s)) return false;
  return s.replace(/,/g, "") === c.replace(/\./g, "");
};

// Mirrors ExplanatoryAnswerMatcher::extractYesNoVerdict — opposite polarity must never upgrade.
export const extractYesNoVerdict = (text) => {
  const parts = String(text ?? "").split(/[.!?]+/).map((p) => p.trim()).filter(Boolean);
  const chunks = (parts.length ? parts : [String(text ?? "")]).map((p) =>
    p.toLowerCase().replace(/[^\p{L}\p{N}]+/gu, " ").replace(/\s+/g, " ").trim()
  );
  for (const chunk of chunks) {
    if (!chunk) continue;
    if (/^(no|nope|false)\b/u.test(chunk)) return "no";
    if (/^(yes|yeah|yep|true)\b/u.test(chunk)) return "yes";
    if (/\bnot correct\b|\bis not correct\b|\bis incorrect\b|\bincorrect\b/u.test(chunk)) return "no";
    if (/\bis correct\b/u.test(chunk) && !/\bnot\b/u.test(chunk)) return "yes";
    if (/\bit is not (?:also )?a multiple\b|\bnot a multiple\b/u.test(chunk)) return "no";
    if (/\bit is (?:also )?a multiple\b/u.test(chunk)) return "yes";
  }
  return null;
};

export const yesNoVerdictConflict = (submitted, correct) => {
  const s = extractYesNoVerdict(submitted);
  const c = extractYesNoVerdict(correct);
  return s != null && c != null && s !== c;
};

// Mirrors AlgebraicExpressionMatcher equation equivalence ("2+A=R+N" ≡ "A=N+R-2").
export const algebraicEquationMatches = (submitted, correct) => {
  const norm = (v) => String(v ?? "")
    .replace(/<[^>]*>/g, " ")
    .replace(/[×⋅·]/g, "*").replace(/÷/g, "/").replace(/[−–—]/g, "-")
    .toLowerCase().replace(/\s+/g, "").trim();
  const signedAtoms = (eq) => {
    if (!eq.includes("=")) return null;
    const i = eq.indexOf("=");
    const lhs = eq.slice(0, i);
    const rhs = eq.slice(i + 1);
    if (!lhs || !rhs || lhs.includes("=") || rhs.includes("=")) return null;
    const atoms = [];
    const addSide = (side, flip) => {
      const s = /^[+-]/.test(side) ? side : `+${side}`;
      for (const m of s.matchAll(/([+-])([^+-]+)/g)) {
        const neg = (m[1] === "-") !== flip;
        atoms.push(`${neg ? "-" : "+"}${m[2]}`);
      }
    };
    addSide(lhs, false);
    addSide(rhs, true);
    return atoms.sort().join("");
  };
  const a = norm(submitted);
  const b = norm(correct);
  if (!a || (!a.includes("=") && !b.includes("="))) return false;
  if (!/^[0-9a-z+\-*/^().=]+$/.test(a) || !/^[0-9a-z+\-*/^().=]+$/.test(b)) return false;
  if (/[a-z]{2,}/.test(a) || /[a-z]{2,}/.test(b)) return false;
  const ka = signedAtoms(a);
  const kb = signedAtoms(b);
  return ka != null && kb != null && ka === kb;
};

export const extractCorrectAlternatives = (cf) => {
  if (cf.includes(" (or) ")) return cf.split(" (or) ").map((p) => p.trim()).filter(Boolean);
  if (/ or /i.test(cf)) return cf.split(/ or /i).map((p) => p.trim()).filter(Boolean);
  if (cf.includes(" / ")) return cf.split(" / ").map((p) => p.trim()).filter(Boolean);
  return [cf];
};

// The stem asks for exactly ONE value ("what number should Julian choose?"), so an "X or Y" mark scheme
// is an either/or. Plural/quantity wording ("write down the even numbers", "two multiples of 2001") means
// the answer IS a list. Mirrors FuzzyAnswerMatching::questionAsksForOneValue.
export const questionAsksForOneValue = (questionText) => {
  const q = decodeHtmlSpaces(String(questionText ?? "").replace(/<[^>]*>/g, " "))
    .toLowerCase()
    .replace(/\s+/g, " ")
    .trim();
  if (!q) return false;
  if (/\b(?:numbers|values|digits|letters|answers|all the|all of|list|both|each|every|pairs?|ways|two|three|four|five|six)\b/.test(q)) return false;
  return /\b(?:what|which|write down|give|state|name)\b[^.?]*?\b(?:a|one|the)?\s*(?:number|value|digit|letter|answer)\b/.test(q);
};

// The submission names two or more of an either/or mark scheme's alternatives ("4 and 5", "4 or 5" for
// "4 or 5") — a hedge covering every option instead of choosing one. Only where every alternative is a
// bare value (a number or a lone letter): prose alternatives are two WORDINGS of one answer ("it gets
// bigger or it increases") and a student may write both.
// Mirrors FuzzyAnswerMatching::submissionHedgesAlternatives.
export const submissionHedgesAlternatives = (submitted, alternatives) => {
  const plain = (v) => decodeHtmlSpaces(String(v ?? "").replace(/<[^>]*>/g, " ")).toLowerCase().replace(/\s+/g, " ").trim();
  const isBareValue = (v) => /^(?:-?\d+(?:[.,]\d+)?|\p{L})$/u.test(v);
  const values = new Set();
  for (const alt of alternatives) {
    const a = plain(alt);
    if (!isBareValue(a)) return false;
    values.add(a);
  }
  if (values.size < 2) return false;
  const named = new Set(
    plain(submitted)
      .split(/\s*(?:,|;|\/|&|\bor\b|\band\b)\s*|\s+/i)
      .map((t) => t.replace(/^[\s.]+|[\s.]+$/g, ""))
      .filter((t) => t !== "" && values.has(t))
  );
  return named.size > 1;
};

// A bare ordinal equals its digit ("3rd" ≈ "3"). Only collapse a standalone ordinal — NOT an ordinal
// that begins a longer phrase, or "1st November 1998" (a date) would wrongly reduce to "1" and match
// any answer starting with "1st".
export const normalizeOrdinal = (v) =>
  v.replace(/^(\d+)(?:st|nd|rd|th)$/, "$1");

// The index named by "<n>th <noun>" / "<noun> <n>th" / "<n>th" / "<n>", else null. Mirrors
// FuzzyAnswerMatching::ordinalIndexValue (PHP) so client and server agree on "9" ≈ "9th pattern".
const ordinalIndexValue = (v, requireOrdinal) => {
  const plain = String(v ?? "")
    .replace(/ /g, " ")
    .replace(/<[^>]*>/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .toLowerCase()
    .replace(/[.:;]+$/, "");
  if (!plain || !/^[a-z0-9 ]+$/.test(plain)) return null;
  const words = plain.split(" ").filter((w) => w && w !== "the");
  if (words.length > 2) return null;
  let number = null;
  let hasOrdinal = false;
  let nouns = 0;
  for (const word of words) {
    const ord = word.match(/^(\d+)(?:st|nd|rd|th)$/);
    if (ord) {
      if (number !== null) return null;
      number = Number(ord[1]);
      hasOrdinal = true;
    } else if (/^\d+$/.test(word)) {
      if (number !== null) return null;
      number = Number(word);
    } else {
      // "5th June" is a date, not an index — its day number alone is not the answer.
      if (/^(?:jan|feb|mar|apr|may|jun|jul|aug|sep|oct|nov|dec)/.test(word)) return null;
      nouns++;
    }
  }
  if (number === null || nouns > 1) return null;
  return requireOrdinal && !hasOrdinal ? null : number;
};

// "Which pattern number is it?" answered "9" against a "9th pattern" mark scheme — the noun only
// restates the question, and the ordinal suffix hides the number from every numeric tier.
export const ordinalIndexAnswerMatches = (submitted, correct) => {
  const c = ordinalIndexValue(correct, true);
  if (c === null) return false;
  const s = ordinalIndexValue(submitted, false);
  return s !== null && s === c;
};

export const extractAnswerCandidates = (value) => {
  const raw = String(value ?? "").trim();
  if (!raw) return [];
  const candidates = [raw];
  if (raw.includes("=")) {
    const parts = raw.split("=");
    const rhs = String(parts[parts.length - 1] ?? "").trim();
    if (rhs) candidates.push(rhs);
  }
  return Array.from(new Set(candidates.filter(Boolean)));
};

// Numeric value of a plain number, accepting a real thousands separator ("1,200" / "1 200" → 1200) but
// NOT a comma-decimal typo ("8,7", "12,24") — a comma is only a separator when exactly 3 digits follow
// it. Mirrors FuzzyAnswerMatching::parseSimpleNumericValue so the client agrees with the server.
export const plainNumericValue = (v) => {
  let s = decodeHtmlSpaces(String(v ?? "")).replace(/<[^>]*>/g, "").trim().toLowerCase();
  if (s === "") return null;
  if (/,(?!\d{3}(?!\d))/.test(s)) return null;
  // Only a valid thousands grouping collapses ("1 000" → 1000). An arbitrary digit split is not a
  // number: "5 04" must not become 504, nor be read as the 5.04 the student failed to type.
  if (/\d\s+\d/.test(s) && !/^[-+]?\d{1,3}(?: \d{3})+(?:\.\d+)?$/.test(s)) return null;
  const bare = s.replace(/[,\s]/g, "");
  if (!/^[-+]?\d+(?:\.\d+)?$/.test(bare)) return null;
  const n = parseFloat(bare);
  return Number.isFinite(n) ? n : null;
};

// The comparison operator an answer states, canonicalised — "=/" and "!=" are both ≠, "<=" is ≤. Null
// when the text is not (just) an operator. "Complete the expression with <, > or =" answers are decided
// EXACTLY: the fuzzy tiers drop the "/" from "=/" (reading a ≠ as "="), and tag-stripping turns a bare
// "<" into an empty string that matches anything else normalising to empty.
// Mirrors FuzzyAnswerMatching::canonicalComparisonOperator.
export const canonicalComparisonOperator = (value) => {
  const v = decodeHtmlSpaces(String(value ?? ""))
    .replace(/^<p>|<\/p>$|<br\s*\/?>/gi, "")
    .replace(/&lt;/gi, "<")
    .replace(/&gt;/gi, ">")
    .replace(/&ne;/gi, "≠")
    .replace(/&le;/gi, "≤")
    .replace(/&ge;/gi, "≥")
    // LaTeX spellings from the math-tools keyboard, resolved before the literal map below so the
    // remaining backslash stays meaningful: "=\" is a struck-through equals (a ≠), not an "=" plus noise.
    .replace(/\\not\s*=/gi, "≠")
    .replace(/\\neq?\b/gi, "≠")
    .replace(/\\leq?\b/gi, "≤")
    .replace(/\\geq?\b/gi, "≥")
    .replace(/\\lt\b/gi, "<")
    .replace(/\\gt\b/gi, ">")
    .replace(/[\s$]+/g, "");
  if (!v) return null;
  const map = {
    "=": "=", "<": "<", ">": ">",
    "≤": "≤", "<=": "≤", "=<": "≤",
    "≥": "≥", ">=": "≥", "=>": "≥",
    // A ≠ is drawn by striking the equals through, and students type that stroke either way.
    "≠": "≠", "!=": "≠", "=!": "≠", "=/": "≠", "/=": "≠", "=/=": "≠", "=\\": "≠", "\\=": "≠", "=\\=": "≠",
  };
  return map[v] ?? null;
};

// A mark scheme naming TWO things joined by "and" — "Birmingham and London", "3 and 5". Both are
// required ("Which TWO cities…"), so half of it — or the unfinished "Birmingham and" — is wrong. Each
// side must be a proper noun or a number, so an explanatory sentence containing "and" isn't a pair.
// Mirrors FuzzyAnswerMatching::correctIsNamedPair.
export const correctIsNamedPair = (correct) => {
  const t = decodeHtmlSpaces(String(correct ?? "")).replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim();
  if (!t || t.includes(",") || /\bor\b/i.test(t)) return null;
  const m = t.match(/^(.+?)\s+and\s+(.+)$/i);
  if (!m) return null;
  const side = /^(?:\p{Lu}[\p{L}'’-]*(?:\s+\p{Lu}[\p{L}'’-]*)?|-?\d+(?:\.\d+)?\s*[\p{L}%°]{0,4})$/u;
  const a = m[1].trim();
  const b = m[2].trim();
  return side.test(a) && side.test(b) ? [a, b] : null;
};

// A sequence of single-letter labels — a route through stations ("A E D B C G"), a reordering
// ("B C A"), a word reflected letter by letter ("S C I T A M E H T A M"). Every letter is part of the
// answer and their ORDER is the answer, so a prefix of it ("A E") is wrong, not "most of the way
// there". Three or more letters: a two-letter answer is too easily an abbreviation.
// "to" is a free connective in prose routes ("B to F to E to D") — strip it before testing, same as
// arrows/commas are stripped in labelSequenceMatches below.
// Mirrors FuzzyAnswerMatching::correctIsLabelSequence.
export const correctIsLabelSequence = (correct) => {
  const t = decodeHtmlSpaces(String(correct ?? "")).replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim();
  const clean = t.replace(/\bto\b/gi, " ").replace(/\s+/g, " ").trim();
  if (!/^(?:\p{L}\s+){2,}\p{L}$/u.test(clean)) return null;
  return clean.replace(/\s+/g, "").toUpperCase();
};

// Separators are free ("AEDBCG", "A, E, D, B, C, G", "A → E → D → B → C → G"); letters and order aren't.
export const labelSequenceMatches = (submitted, correct) => {
  const expected = correctIsLabelSequence(correct);
  if (!expected) return false;
  const s = decodeHtmlSpaces(String(submitted ?? ""))
    .replace(/<[^>]*>/g, " ")
    .replace(/\bto\b/gi, " ")
    .replace(/(?:->|→|[\s,;.\-–—>])+/gu, "")
    .toUpperCase();
  return s !== "" && s === expected;
};

// Hyphenated multi-letter edge route ("AB-BC-CE-EG-GF-FH"). Mirrors FuzzyAnswerMatching::correctIsEdgeRoute.
export const correctIsEdgeRoute = (correct) => {
  const t = decodeHtmlSpaces(String(correct ?? "")).replace(/<[^>]*>/g, " ").replace(/\s+/g, "").trim();
  if (!/^(?:\p{L}{1,4})(?:[-–—]\p{L}{1,4}){2,}$/u.test(t)) return null;
  const parts = t.split(/[-–—]+/u).map((p) => p.trim().toUpperCase()).filter(Boolean);
  return parts.length >= 3 ? parts : null;
};

// The chain of vertices an edge route visits (["AB","BC","CE"] → A,B,C,E), or null when the segments
// are not two-letter edges that join up. Mirrors FuzzyAnswerMatching::vertexPathForEdgeRoute.
export const vertexPathForEdgeRoute = (edges) => {
  const path = [];
  for (let i = 0; i < edges.length; i++) {
    const edge = String(edges[i] ?? "");
    if (edge.length !== 2) return null;
    if (i === 0) path.push(edge[0]);
    else if (path[path.length - 1] !== edge[0]) return null;
    path.push(edge[1]);
  }
  return path.length >= 3 ? path : null;
};

export const edgeRouteMatches = (submitted, correct) => {
  const expected = correctIsEdgeRoute(correct);
  if (!expected) return false;
  const s = decodeHtmlSpaces(String(submitted ?? "")).replace(/<[^>]*>/g, " ").replace(/\s+/g, "").trim();
  const parts = s.split(/[-–—/→>]+/u).map((p) => p.trim().toUpperCase()).filter(Boolean);
  if (parts.length === expected.length && parts.every((p, i) => p === expected[i])) return true;
  // The same route named by the villages it passes through ("A-B-C-E-G-F-H", "ABCEGFH") rather than by
  // the roads it uses — one journey, so only the order of the letters carries meaning.
  const letters = s.replace(/[^\p{L}]+/gu, "").toUpperCase();
  if (!letters) return false;
  if (letters === expected.join("")) return true;
  const vertices = vertexPathForEdgeRoute(expected);
  return !!vertices && letters === vertices.join("");
};

export const isMoneyDenominationList = (value) => {
  const v = decodeHtmlSpaces(String(value ?? "")).replace(/<[^>]*>/g, " ");
  const tokens = v.match(/(?:£\s*\d+(?:\.\d+)?|\d+(?:\.\d+)?\s*p)\b/gi) || [];
  return tokens.length >= 2;
};

export const moneyDenominationListMatches = (submitted, correct) => {
  const canon = (raw) => {
    const v = decodeHtmlSpaces(String(raw ?? "")).replace(/<[^>]*>/g, " ");
    const tokens = v.match(/(?:£\s*\d+(?:\.\d+)?|\d+(?:\.\d+)?\s*p)\b/gi) || [];
    return tokens.map((t) => {
      const x = t.replace(/\s+/g, "").toLowerCase();
      if (x.startsWith("£")) {
        const n = Number(x.slice(1));
        return `£${Number.isFinite(n) ? String(n) : x.slice(1)}`;
      }
      const n = Number(x.replace(/p$/i, ""));
      return `${Number.isFinite(n) ? String(n) : x}p`;
    }).sort();
  };
  const s = canon(submitted);
  const c = canon(correct);
  return s.length > 0 && s.length === c.length && s.every((t, i) => t === c[i]);
};

// Both names present, in any order — "London and Birmingham" answers "Birmingham and London".
export const namedPairMatches = (submitted, pair) => {
  const s = decodeHtmlSpaces(String(submitted ?? "")).replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim().toLowerCase();
  if (!s) return false;
  return pair.every((item) => {
    const needle = item.trim().toLowerCase().replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    return new RegExp(`(?<![\\p{L}\\d])${needle}(?![\\p{L}\\d])`, "u").test(s);
  });
};

// A sum of two or more measured values: "50p+10p", "20p + 20p + 20p", "3 + 4". The suffix must be a
// UNIT, not an algebra variable. Mirrors FuzzyAnswerMatching::isSumExpression.
const SUM_TERM = String.raw`\d+(?:\.\d+)?\s*(?:p|pence|pounds?|kg|mg|g|km|cm|mm|m|ml|l|%|°c?|²|³)?`;
export const isSumExpression = (value) => {
  const t = decodeHtmlSpaces(String(value ?? "")).replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim()
    .replace(/[\s,.;]+$/, "");
  if (!t || !t.includes("+")) return false;
  return new RegExp(`^${SUM_TERM}(?:\\s*\\+\\s*${SUM_TERM})+$`, "i").test(t);
};

// Terms of a sum, sorted — "20p + 10p + 20p" and "20p + 20p + 10p" are the same answer.
export const sumExpressionCanonical = (value) =>
  decodeHtmlSpaces(String(value ?? "")).replace(/<[^>]*>/g, " ").toLowerCase()
    .replace(/[\s,.;]+$/, "")
    .split("+")
    .map((p) => p.replace(/\s+/g, ""))
    .filter(Boolean)
    .sort()
    .join("+");

// A mark scheme that lists two or more sums ("List all the ways Penny can make 60p") — every one of
// them is part of the answer, so a subset of the list is partial, never correct.
export const correctIsSumEnumeration = (correct) => {
  const parts = splitMultiValueAnswer(String(correct ?? ""));
  return parts.length > 1 && parts.every(isSumExpression);
};

export const sumEnumerationMatches = (submitted, correct) => {
  const canonSet = (v) => Array.from(new Set(
    splitMultiValueAnswer(String(v ?? "")).map(sumExpressionCanonical).filter(Boolean)
  ));
  const expected = canonSet(correct);
  const given = canonSet(submitted);
  return given.length === expected.length && expected.every((e) => given.includes(e));
};

// Fold <sup>n</sup> / unicode superscripts / 10^{2} to caret form before tag-stripping would glue
// "1<sup>3</sup>" into "13". Mirrors AlgebraicExpressionMatcher::normalize (PHP).
export const normalizeNumericPowerExpr = (v) => {
  let t = String(v ?? "");
  t = t.replace(/<sup[^>]*>\s*([^<]*?)\s*<\/sup>/gi, "^$1");
  t = decodeHtmlEntities(decodeHtmlSpaces(t).replace(/<[^>]*>/g, " "));
  const supMap = {
    "⁰": "^0", "¹": "^1", "²": "^2", "³": "^3", "⁴": "^4",
    "⁵": "^5", "⁶": "^6", "⁷": "^7", "⁸": "^8", "⁹": "^9",
  };
  t = t.replace(/[⁰¹²³⁴⁵⁶⁷⁸⁹]/g, (ch) => supMap[ch] ?? ch);
  t = t.replace(/\^(\d)\^(\d)/g, "^$1$2");
  t = t.replace(/\^\s*\{\s*([^{}]*)\s*\}/g, "^$1");
  t = t.replace(/[×⋅·]/g, "*").replace(/÷/g, "/").replace(/[−–—]/g, "-");
  t = t.replace(/\s+/g, " ").trim().toLowerCase().replace(/[=.\s]+$/g, "");
  return t;
};

export const isNumericPowerExpression = (correct) => {
  const t = normalizeNumericPowerExpr(correct);
  return t !== ""
    && t.includes("^")
    && /^[0-9+\-*/^().= ]+$/.test(t)
    && /\d/.test(t);
};

const powerCountOf = (normalized) => (normalized.match(/\^/g) || []).length;

const equationSegmentsOf = (normalized) =>
  normalized.split("=").map((p) => p.trim()).filter((p) => p !== "");

// A leading sign is not a binary operator.
const operatorMultisetOf = (normalized) => {
  const ops = [...normalized.matchAll(/(?<=[\d).])\s*([+\-*/])/g)].map((m) => m[1]);
  ops.sort();
  return ops.join(",");
};

const exponentMultisetOf = (normalized) => {
  const exps = [...normalized.matchAll(/\^(\d+(?:\.\d+)?)/g)].map((m) => m[1]);
  exps.sort();
  return exps.join(",");
};

// Lightweight evaluator for digit/operator/bracket/^ expressions (no variables). Returns null if unparseable.
const evalNumericPowerExpr = (expr) => {
  const s = String(expr ?? "").replace(/\s+/g, "");
  if (!s || !/^[0-9+\-*/^().]+$/.test(s)) return null;
  let pos = 0;
  const peek = () => (pos < s.length ? s[pos] : "");
  const parseExpr = () => {
    let value = parseTerm();
    if (value === null) return null;
    while (peek() === "+" || peek() === "-") {
      const op = peek();
      pos++;
      const rhs = parseTerm();
      if (rhs === null) return null;
      value = op === "+" ? value + rhs : value - rhs;
    }
    return value;
  };
  const parseTerm = () => {
    let value = parseFactor();
    if (value === null) return null;
    while (true) {
      const c = peek();
      if (c === "*" || c === "/") {
        pos++;
        const rhs = parseFactor();
        if (rhs === null) return null;
        if (c === "/") {
          if (Math.abs(rhs) < 1e-12) return null;
          value /= rhs;
        } else {
          value *= rhs;
        }
        continue;
      }
      if (c === "(" || /\d/.test(c)) {
        const rhs = parseFactor();
        if (rhs === null) return null;
        value *= rhs;
        continue;
      }
      return value;
    }
  };
  const parseFactor = () => {
    const c = peek();
    if (c === "+" || c === "-") {
      pos++;
      const v = parseFactor();
      return v === null ? null : (c === "-" ? -v : v);
    }
    let base = null;
    if (c === "(") {
      pos++;
      base = parseExpr();
      if (base === null || peek() !== ")") return null;
      pos++;
    } else if (/\d|\./.test(c)) {
      const start = pos;
      while (pos < s.length && /[\d.]/.test(s[pos])) pos++;
      const num = Number(s.slice(start, pos));
      if (!Number.isFinite(num)) return null;
      base = num;
    } else {
      return null;
    }
    if (peek() === "^") {
      pos++;
      const exp = parseFactor();
      if (exp === null) return null;
      base **= exp;
    }
    return base;
  };
  const value = parseExpr();
  return (value !== null && pos === s.length && Number.isFinite(value)) ? value : null;
};

// The value of each "=" segment, or null when any of them doesn't parse. A chain states one value
// several ways ("50000 × 2 = 100000"), so matching ANY segment is enough to say the sums agree.
const calculationValuesOf = (normalized) => {
  const out = [];
  for (const seg of equationSegmentsOf(normalized)) {
    const v = evalNumericPowerExpr(seg);
    if (v === null) return null;
    out.push(v);
  }
  return out.length ? out : null;
};

// The mark scheme is a bracketed calculation — "(552 + 448) × (552 - 448)", "(30 × 10) ÷ 50" — where the
// brackets carry the meaning. Mirrors AlgebraicExpressionMatcher::isBracketedCalculation.
export const isBracketedCalculation = (correct) => {
  const t = normalizeNumericPowerExpr(correct);
  return t !== ""
    && t.includes("(")
    && /^[0-9+\-*/^().= ]+$/.test(t)
    && /[+\-*/]/.test(t)
    && calculationValuesOf(t) !== null;
};

// The submission is a calculation worth a DIFFERENT amount from a bracketed mark scheme — the signature
// of dropped or moved brackets ("25001 - 24999 × 25001 + 24999" for "(25001 + 24999) × (25001 - 24999)"),
// which every tier that compares the digits and symbols present reads as the same sum. Brackets that make
// no difference to the value ("3 + (4 × 5)") leave the two equal and are never downgraded.
// Mirrors AlgebraicExpressionMatcher::bracketedCalculationMismatch.
export const bracketedCalculationMismatch = (submitted, correct) => {
  if (!isBracketedCalculation(correct)) return false;
  const a = normalizeNumericPowerExpr(submitted);
  if (a === "" || !/^[0-9+\-*/^().= ]+$/.test(a)) return false;
  const want = calculationValuesOf(normalizeNumericPowerExpr(correct));
  if (!want) return false;
  // A submission built only of digits, operators and brackets that still doesn't parse has a bracket
  // missing or misplaced ("8.5+7.5)×8.5-7.5") — the same defect as dropping them, and the token tiers
  // read it as the mark scheme's own sum because they only count symbols.
  const got = calculationValuesOf(a);
  if (!got) return /\d/.test(a);
  return !got.some((x) => want.some((y) => Math.abs(x - y) <= 1e-9 * Math.max(1, Math.abs(y))));
};

// Mirrors AlgebraicExpressionMatcher::matchesNumericPower — form matters, not just evaluated value.
export const matchesNumericPower = (submitted, correct) => {
  const a = normalizeNumericPowerExpr(submitted);
  const b = normalizeNumericPowerExpr(correct);
  if (!a || !a.includes("^")) return false;
  if (a.replace(/\s+/g, "") === b.replace(/\s+/g, "")) return true;
  // "1+3+5+7=16=4²" is a chain of equal expressions, not one expression — the working on each side of
  // every "=" is part of the answer, so it is graded segment by segment. Mirrors
  // AlgebraicExpressionMatcher::matchesEquationChain.
  if (a.includes("=") || b.includes("=")) {
    const segsA = equationSegmentsOf(a);
    const segsB = equationSegmentsOf(b);
    if (!segsA.length || segsA.length !== segsB.length) return false;
    return segsB.every((segB, i) => {
      const segA = segsA[i];
      if (segA.replace(/\s+/g, "") === segB.replace(/\s+/g, "")) return true;
      if (powerCountOf(segA) !== powerCountOf(segB)) return false;
      if (exponentMultisetOf(segA) !== exponentMultisetOf(segB)) return false;
      if (operatorMultisetOf(segA) !== operatorMultisetOf(segB)) return false;
      const va = evalNumericPowerExpr(segA);
      const vb = evalNumericPowerExpr(segB);
      if (va === null || vb === null) return false;
      return Math.abs(va - vb) <= 1e-9 * Math.max(1, Math.abs(vb));
    });
  }
  if (powerCountOf(a) !== powerCountOf(b)) return false;
  if (exponentMultisetOf(a) !== exponentMultisetOf(b)) return false;
  const va = evalNumericPowerExpr(a);
  const vb = evalNumericPowerExpr(b);
  if (va === null || vb === null) return false;
  return Math.abs(va - vb) <= 1e-9 * Math.max(1, Math.abs(vb));
};

// "33 %" / "33 percent" / "33 per cent" → "33%". Only rewrites a sign or word that is already there.
const canonicalPercentSign = (v) =>
  String(v ?? "").replace(/\s*(?:%|\bper\s*cent(?:age)?\b)/gi, "%").trim();

export const singleValueMatches = (submitted, correct, questionText = "") => {
  submitted = canonicalizeDegreeGlyphs(submitted);
  correct = canonicalizeDegreeGlyphs(correct);
  // The whole answer IS a unit noun ("Andy is 183 ___ tall" -> "centimetres") — "cm" must match.
  if (unitNounEquivalent(submitted, correct)) return true;
  // The operator IS the answer ("complete with <, > or =") — compare it exactly, before the tiers below
  // normalise "=/" into "=" or reduce a bare "<" to nothing.
  const expectedOperator = canonicalComparisonOperator(correct);
  if (expectedOperator) return canonicalComparisonOperator(submitted) === expectedOperator;

  // Power mark schemes are decided on form before sum / unit / digit-reducing tiers.
  if (isNumericPowerExpression(correct)) {
    return matchesNumericPower(submitted, correct);
  }

  // "15/2" for "7 1/2" — the same value in the form these papers rule out; every numeric tier below
  // compares by value and accepts it. "November 1" for a bare "November" month scheme, likewise.
  if (submittedIsImproperFractionForMixedScheme(submitted, correct, questionText)) return false;
  if (submittedAddsDayNumberToMonthScheme(submitted, correct)) return false;

  // "5 04" for "£5.04", "3 23" for "3.24" — the space is not a decimal point. Decided before the
  // clock-time and numeric tiers, which both read it as the missing separator.
  if (submittedSpacesDecimalPoint(submitted, correct)) return false;

  // "33 %", "33&nbsp;%" and "33 percent" all ARE the mark scheme's "33%" — no tier below normalises the
  // sign, so a space in front of it (the math-tools palette inserts one) graded wrong on the take page
  // while the backend, which strips it, said correct. The sign is still required: a bare "33" is left
  // untouched here and still fails against a "33%" scheme.
  const s = canonicalPercentSign(normalizeVal(stripMarkSchemeOperatorPrefix(submitted)));
  const c = canonicalPercentSign(normalizeVal(stripMarkSchemeOperatorPrefix(correct)));
  if (!s || !c) return false;

  // "counter-clockwise" is the OPPOSITE of "clockwise", and "(30 × 10) \ 50" is not "÷ 50" — both are
  // wrong before any tier below gets to normalise them into a match.
  if (rotationDirectionConflict(submitted, correct) || hasStrayBackslash(submitted)) return false;
  if (rotationDirectionsAgree(submitted, correct)) return true;

  // "Likely" for a "Very likely" mark scheme — a neighbouring rung of the printed probability scale.
  if (likelihoodLabelConflict(submitted, correct)) return false;

  // Two sums differ by how many times a term appears ("20p + 20p" is not "20p + 20p + 20p"), which the
  // unit-stripping and no-space tiers below throw away. Compare the terms as a multiset instead.
  // Authoritative when the mark scheme is a sum: a dropped "+" is wrong.
  if (isSumExpression(correct)) {
    return isSumExpression(submitted) && sumExpressionCanonical(submitted) === sumExpressionCanonical(correct);
  }

  // Coin lists that name a £ amount — "1p" is not "£1".
  if (isMoneyDenominationList(correct)) {
    return moneyDenominationListMatches(submitted, correct);
  }

  // Hyphenated edge routes — every segment in order.
  if (correctIsEdgeRoute(correct)) {
    return edgeRouteMatches(submitted, correct);
  }

  // Dot notation is ambiguous: "4.33" is both 4:33 and 13/3 rounded to 2 d.p. The clock branch owns
  // every d.dd scheme, so a division that doesn't terminate marked its own longer form ("4.3333")
  // wrong. Settle the decimal reading first, for a scheme with no colon and no am/pm to make it
  // unambiguously a time.
  if (!/:/.test(c) && !/[ap]\.?\s*m\b/i.test(c)
    && (roundedDecimalPrecisionMatch(submitted, correct) || recurringDecimalExtensionMatch(submitted, correct))) {
    return true;
  }

  if (correctIsClockTime(c)) return timesMatch(submitted, correct, questionAsksForClockTime(questionText));

  if (s === c) return true;

  // The mark scheme is JUST a number (a count/value, no unit) — the answer must be a plain number too.
  // Reject anything with an appended unit or word ("18m", "18 cm", "18 apples") that the unit-stripping
  // tiers below would otherwise reduce to the bare number and accept.
  if (isPlainNumberVal(c) && !isPlainNumberVal(s)) return false;

  // A leading zero ("0800", "0730") marks the value as a 24-hour time / code, not an arithmetic
  // quantity — the numeric-equality tier below reads "800" and "0800" as the same number and would
  // accept a dropped leading digit as if it were a harmless thousands-formatting difference.
  if (isPlainNumberVal(c) && /^0\d/.test(c) && s !== c) return false;

  // Both sides are plain numbers — compare by value so a thousands separator ("1,200" vs "1200") matches.
  // The comma-decimal guard in plainNumericValue keeps "8,7" from being read as 87.
  if (isPlainNumberVal(c) && isPlainNumberVal(s)) {
    const sn = plainNumericValue(s);
    const cn = plainNumericValue(c);
    if (sn !== null && cn !== null && Math.abs(sn - cn) < 1e-9) return true;
  }

  // Mark scheme to N d.p. ("4.7") — a more precise working value that rounds to it ("4.71") is accepted.
  if (roundedDecimalPrecisionMatch(submitted, correct)) return true;

  // A recurring decimal written to more or fewer places than the scheme ("4.3333" / "0.33" for "0.333").
  if (recurringDecimalExtensionMatch(submitted, correct)) return true;

  // Strip prime/apostrophe notation so A′ matches A (coordinate geometry labelling).
  const sP = stripPrime(s);
  const cP = stripPrime(c);
  if (sP && cP && sP === cP) return true;

  const sF = normalizeVal(normalizeLatexFraction(s));
  const cF = normalizeVal(normalizeLatexFraction(c));
  if (sF === cF) return true;
  if (timesMatch(sF, cF)) return true;
  const sU = stripCommonUnit(sF);
  const cU = stripCommonUnit(cF);
  if (sU !== "" && sU === cU) return true;
  if (isUnitEquivalent(submitted, correct)) return true;
  const sO = normalizeOrdinal(sF);
  const cO = normalizeOrdinal(cF);
  if (sF !== "" && sO === cO) return true;
  if (ordinalIndexAnswerMatches(submitted, correct)) return true;

  const correctAlts = extractCorrectAlternatives(cF);
  const altMatcher = (sp, ca) =>
    sp === ca ||
    stripPrime(sp) === stripPrime(ca) ||
    timesMatch(sp, ca) ||
    (stripCommonUnit(sp) !== "" && stripCommonUnit(sp) === stripCommonUnit(ca)) ||
    normalizeOrdinal(sp) === normalizeOrdinal(ca) ||
    isUnitEquivalent(sp, ca);

  if (correctAlts.some((ca) => altMatcher(sF, ca))) return true;
  if (correctAlts.length > 1) return false;

  const submittedCandidates = extractAnswerCandidates(submitted)
    .map((v) => normalizeVal(stripMarkSchemeOperatorPrefix(v)))
    .filter(Boolean);
  for (const sc of submittedCandidates) {
    if (sc === cF) return true;
    if (timesMatch(sc, cF)) return true;
    if (stripCommonUnit(sc) !== "" && stripCommonUnit(sc) === stripCommonUnit(cF)) return true;
    if (normalizeOrdinal(sc) === normalizeOrdinal(cF)) return true;
    if (isUnitEquivalent(sc, cF)) return true;
  }

  // A lone number mistyped with a comma decimal separator ("3,00", "£3,5") must NOT be salvaged by
  // splitting on the comma and matching its first fragment ("3") — it's one malformed number, not a
  // value list. Skip the fragment fallback for it so "3,00" fails against a "£3.00"/"3" mark scheme.
  if (!isSingleMalformedNumber(submitted)) {
    const submittedParts = splitMultiValueAnswer(sF);
    if (submittedParts.some((sp) => altMatcher(sp, cF))) return true;
  }

  const sN = normalizeHyphensAndBrackets(submitted);
  const cN = normalizeHyphensAndBrackets(correct);
  if (sN && cN && sN === cN) return true;
  if (extractCorrectAlternatives(cN).some((ca) => sN === ca)) return true;

  // Last resort: a word-boundary typo ("Super market" vs "Supermarket") — compare with all
  // whitespace removed. Tried only after every other tier fails, so genuinely distinct
  // multi-word answers (which would already have matched above if equivalent) are unaffected.
  // Word answers only: collapsing spaces between digits changes numeric meaning, so a bare fraction
  // "17/11" must not match the mixed number "1 7/11" (a different value, 17/11 ≠ 18/11) by fusing
  // away the whole-number/fraction separator space. Mirrors the equivalent guard in
  // FuzzyAnswerMatching::isFuzzyTextMatchSingle (PHP) so the two stay in agreement.
  const sNoSpace = s.replace(/\s+/g, "");
  const cNoSpace = c.replace(/\s+/g, "");
  if (sNoSpace && cNoSpace && sNoSpace === cNoSpace
    && /\p{L}/u.test(sNoSpace) && /\p{L}/u.test(cNoSpace)
    && !/\d\s+\d/.test(s) && !/\d\s+\d/.test(c)) return true;

  return false;
};

export const isAnswerPartial = (submitted, correct, questionText = "") => {
  if (submitted == null || submitted === "" || submitted === "—") return false;
  if (correct == null || correct === "" || correct === "—") return false;
  if (countQuestionNumericAnswerEquivalent(submitted, correct, questionText)) return false;
  const correctStr = String(correct);
  if (extractCorrectAlternatives(correctStr).length > 1) return false;
  const correctParts = splitMultiValueAnswer(correctStr);
  if (correctParts.length <= 1) return false;
  const submittedParts = splitMultiValueAnswer(String(submitted));
  if (submittedParts.length >= correctParts.length) return false;
  return submittedParts.some((sp) => correctParts.some((cp) => singleValueMatches(sp, cp)));
};

// A review-row answer value may be a multi-fill array or a TypeDraw { text, drawing } object
// rather than a plain string — reduce it to the comparable text form.
export const answerValueText = (value) => {
  if (value != null && typeof value === "object" && !Array.isArray(value)) {
    if ("text" in value || "drawing" in value) return value.text ?? "";
  }
  return value;
};

// Array-aware wrappers used by review pages, where multi-fill answers arrive as arrays that
// must match positionally (blank i ↔ correct[i]) — a value in the wrong box scores nothing.
export const isAnswerCorrectFlexible = (submitted, correct, options = {}) => {
  const submittedVal = answerValueText(submitted);
  if (submittedVal == null || submittedVal === "" || submittedVal === "—") return false;
  if (correct == null || correct === "" || correct === "—") return false;
  // Each blank is compared with isAnswerCorrect, not singleValueMatches: ONE blank can hold a list of
  // values ("66°, 66°" for "the two other angles"), and only isAnswerCorrect matches those as a set.
  // singleValueMatches compares each of the student's values against the whole mark scheme, so it
  // failed even the authored answer — and the blanks stay positional either way (blank i ↔ correct[i]),
  // so a value in the wrong box still scores nothing.
  if (Array.isArray(submittedVal) && Array.isArray(correct)) {
    if (correct.length === 0) return false;
    return correct.every((c, i) => isAnswerCorrect(submittedVal[i] ?? "", c, options));
  }
  if (Array.isArray(submittedVal)) return isAnswerCorrect(submittedVal[0] ?? "", correct, options);
  if (Array.isArray(correct)) return isAnswerCorrect(submittedVal, correct[0] ?? "", options);
  return isAnswerCorrect(submittedVal, correct, options);
};

export const isAnswerPartialFlexible = (submitted, correct) => {
  const submittedVal = answerValueText(submitted);
  if (Array.isArray(submittedVal) || Array.isArray(correct)) {
    const sub = Array.isArray(submittedVal) ? submittedVal : [submittedVal];
    const cor = Array.isArray(correct) ? correct : [correct];
    if (cor.length <= 1) return false;
    let matched = 0;
    cor.forEach((c, i) => {
      if (singleValueMatches(sub[i] ?? "", c)) matched++;
    });
    return matched > 0 && matched < cor.length;
  }
  return isAnswerPartial(submittedVal, correct);
};

// The question demands the fraction be reduced ("Write your answer in its simplest form", "give your
// answer in its lowest terms"). Simplifying IS the task, so an unreduced equivalent — 6/8 for 3/4 — is
// a wrong answer despite having the same numeric value, and the value-equivalence tiers must not
// accept it. Mirrors FuzzyAnswerMatching::questionAsksForSimplestForm on the backend.
export const questionAsksForSimplestForm = (question) => {
  if (!question) return false;
  const text = decodeHtmlSpaces(String(question.question_text || question.text || question.question || ""))
    .replace(/<[^>]*>/g, " ")
    .replace(/\s+/g, " ")
    .toLowerCase();
  if (!text) return false;
  return /\bsimplest\s+form\b/.test(text)
    || /\blowest\s+terms\b/.test(text)
    || /\bsimplif(?:y|ied|ying)\b/.test(text)
    || /\bfully\s+reduced\b/.test(text);
};

// The question demands the answer be WRITTEN as a fraction ("What fraction of …?", "give your answer as
// a fraction"). Expressing it IS the task, so the equal-valued decimal (0.25 for 1/4) is the wrong form —
// but the client's value-equality fraction tier accepts it, and so does the backend's AI grader. Distinct
// from questionAsksForSimplestForm, which polices how far a fraction is reduced.
// Mirrors FuzzyAnswerMatching::questionAsksForFractionForm.
export const questionAsksForFractionForm = (question) => {
  if (!question) return false;
  const text = decodeHtmlSpaces(String(question.question_text || question.text || question.question || ""))
    .replace(/<[^>]*>/g, " ")
    .replace(/\s+/g, " ")
    .toLowerCase();
  if (!text) return false;
  return /\b(?:what|which)\s+fraction\b/.test(text)
    || /\bas\s+a\s+fraction\b/.test(text)
    || /\bin\s+fraction\s+form\b/.test(text);
};

const plainAnswerText = (v) =>
  decodeHtmlEntities(decodeHtmlSpaces(String(v ?? "")).replace(/<[^>]*>/g, " ")).replace(/\s+/g, " ").trim();

// A percentage mark scheme ("33%") answered with the bare number ("33"): 33 and 33% are different
// quantities, and when the answer is one of a listed set ("… 0.35, 33%") the sign IS the value's
// identity — but every numeric tier strips the % and accepted it. The sign is only optional where the
// question asks FOR a percentage (a fraction/decimal/percentage conversion table, whose column header
// carries it). Mirrors FuzzyAnswerMatching::correctIsBarePercentValue + questionExpectsPercentageAnswer.
export const submittedMissesPercentSign = (submitted, correct, question) => {
  const c = plainAnswerText(correct);
  if (!/^-?\d+(?:\.\d+)?\s*%$/.test(c)) return false;
  const s = plainAnswerText(submitted);
  if (s === "" || s.includes("%") || /\bper\s*cent(?:age)?\b/i.test(s)) return false;
  const text = plainAnswerText(question?.question_text || question?.text || question?.question || "").toLowerCase();
  if (!text) return false;
  return !(/(?:as|in|into|to)\s+(?:a\s+)?percentage/.test(text)
    || /what\s+percentage/.test(text)
    || /percentage\s+(?:of|is|equivalent|column)/.test(text)
    || /complete\s+the\s+(?:table|chart)/.test(text)
    || /fill\s+in\s+the\s+(?:table|missing|gaps)/.test(text)
    || /missing\s+(?:value|number|percentage)s?/.test(text)
    || /written\s+out\s+as\s+a\s+fraction/.test(text)
    || /fraction[,\s]+a\s+decimal\s+and\s+a\s+percentage/.test(text)
    || /convert/.test(text));
};

// A value that IS a fraction — "3/4", "1 1/2", "\frac{3}{4}" — as opposed to its decimal value.
// Mirrors FuzzyAnswerMatching::valueIsFraction.
export const valueIsFraction = (v) => {
  const t = plainAnswerText(v).toLowerCase();
  return /^-?\d+\s*\/\s*\d+$/.test(t) || /^-?\d+\s+\d+\s*\/\s*\d+$/.test(t) || t.includes("\\frac");
};

// A backslash that isn't opening a LaTeX command or delimiter. There is no arithmetic a student can
// mean by it: "(30 × 10) \ 50" is not "(30 × 10) ÷ 50".
export const hasStrayBackslash = (v) => /\\(?![a-zA-Z()[\]{}\\])/.test(plainAnswerText(v));

// clockwise vs anticlockwise/counter-clockwise — the same word set, the opposite answer. Every
// word-containment tier reads "clockwise" as a substring of "counter-clockwise" and says yes.
const rotationDirection = (v) => {
  const t = plainAnswerText(v).toLowerCase().replace(/[^a-z]/g, "");
  if (!t) return null;
  // Checked first: "anticlockwise" and "counterclockwise" both CONTAIN "clockwise".
  if (t.includes("anticlockwise") || t.includes("counterclockwise") || t.includes("ccw")) return "anticlockwise";
  if (t.includes("clockwise") || t === "cw") return "clockwise";
  return null;
};

export const rotationDirectionConflict = (submitted, correct) => {
  const s = rotationDirection(submitted);
  const c = rotationDirection(correct);
  return s !== null && c !== null && s !== c;
};

// The same families are also synonyms of each other ("anticlockwise" = "counter-clockwise" = "ccw"),
// which no tier below knows. Only when the whole value IS the direction word: "turns clockwise 5 times"
// carries more than the direction, so it stays for the normal tiers to judge.
const pureRotationDirection = (v) => {
  const t = plainAnswerText(v).toLowerCase().replace(/[^a-z]/g, "");
  return ["anticlockwise", "counterclockwise", "ccw", "clockwise", "cw"].includes(t) ? rotationDirection(t) : null;
};

export const rotationDirectionsAgree = (submitted, correct) => {
  const s = pureRotationDirection(submitted);
  return s !== null && s === pureRotationDirection(correct);
};

// The probability scale is a closed vocabulary printed in the question's word box whose rungs differ
// only by a qualifier — "likely" sits inside "very likely". Every containment tier reads the weaker
// label as agreeing with the stronger one, so scale answers are settled on the vocabulary alone.
// Mirrors ExplanatoryAnswerMatcher::likelihoodLabelConflict.
const LIKELIHOOD_LABELS = {
  impossible: "impossible", "no chance": "impossible", never: "impossible",
  "very unlikely": "very unlikely", unlikely: "unlikely",
  evens: "evens", even: "evens", "even chance": "evens", "equally likely": "evens",
  "50 50": "evens", "fifty fifty": "evens",
  likely: "likely", "very likely": "very likely",
  certain: "certain", definite: "certain", always: "certain",
};

const likelihoodLabel = (v) => {
  const t = plainAnswerText(v).toLowerCase().replace(/[^a-z0-9]+/g, " ").trim();
  return LIKELIHOOD_LABELS[t] ?? null;
};

export const likelihoodLabelConflict = (submitted, correct) => {
  const s = likelihoodLabel(submitted);
  if (s === null) return false;
  // "Certain or Very likely" — every alternative must be a scale label, or this isn't a scale answer.
  const alts = plainAnswerText(correct).split(/\s+\(?or\)?\s+|\s+\/\s+/i).map(likelihoodLabel);
  return alts.length > 0 && !alts.includes(null) && !alts.includes(s);
};

// A range mark scheme ("between 4 and 8 …") is only answered by BOTH endpoints — naming one ("4", or
// the unfinished "4 and") is a different, wrong claim about the range.
export const submittedMissesRangeEndpoints = (submitted, correct) => {
  const c = plainAnswerText(correct).toLowerCase();
  // "Any decimal or fraction between 0.3 and 0.4" states the ACCEPTING SET, not the answer: one value
  // inside it ("0.35") is a complete answer, and demanding both endpoints fails every right one.
  if (/\b(?:any|anything|all)\b[^.]{0,60}\b(?:between|from)\s+-?\d/.test(c)) return false;
  const m = c.match(/\b(?:between|from)\s+(-?\d+(?:\.\d+)?)\s*(?:and|to|–|-)\s*(-?\d+(?:\.\d+)?)/);
  if (!m) return false;
  const s = plainAnswerText(submitted);
  return [m[1], m[2]].some((endpoint) => {
    const esc = endpoint.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    return !new RegExp(`(?<![\\d.])${esc}(?![\\d.])`).test(s);
  });
};

// A submission cut off mid-phrase ("4 and", "between 4 and") — the student never finished it. A
// sentence-completion mark scheme ends on the same connective by design ("is less likely to happen
// than", picked from a printed word box), so the student's exact answer is not a fragment.
// Mirrors FuzzyAnswerMatching::submittedIsDanglingFragment.
export const submittedIsDanglingFragment = (submitted, correct = "") => {
  const tail = (v) => plainAnswerText(v).toLowerCase().replace(/[\s.,;:]+$/, "");
  const s = tail(submitted);
  const m = s === "" ? null : s.match(/\b(and|or|to|of|with|than|between|from|plus|minus|because)$/);
  if (!m) return false;
  const c = correct ? tail(correct) : "";
  return c === "" || !new RegExp(`\\b${m[1]}$`).test(c);
};

const NUMBER_WORD_UNITS = {
  zero: 0, nought: 0, one: 1, two: 2, three: 3, four: 4, five: 5, six: 6, seven: 7, eight: 8, nine: 9,
  ten: 10, eleven: 11, twelve: 12, thirteen: 13, fourteen: 14, fifteen: 15, sixteen: 16, seventeen: 17,
  eighteen: 18, nineteen: 19, twenty: 20, thirty: 30, forty: 40, fourty: 40, fifty: 50, sixty: 60,
  seventy: 70, eighty: 80, ninety: 90,
};
const NUMBER_WORD_SCALES = { thousand: 1000, million: 1000000, billion: 1000000000 };

// A whole number written out in words ("ninety-two thousand five hundred") as its value, or null when
// the text is not one. Mirrors FuzzyAnswerMatching::numberWordsValue.
export const numberWordsValue = (text) => {
  const words = plainAnswerText(text).toLowerCase().replace(/[-,]/g, " ").split(/\s+/)
    .filter((w) => w !== "" && w !== "and");
  if (words.length === 0) return null;
  let total = 0;
  let current = 0;
  for (const word of words) {
    if (word in NUMBER_WORD_UNITS) { current += NUMBER_WORD_UNITS[word]; continue; }
    if (word === "hundred") { current = Math.max(1, current) * 100; continue; }
    if (!(word in NUMBER_WORD_SCALES)) return null;
    total += Math.max(1, current) * NUMBER_WORD_SCALES[word];
    current = 0;
  }
  return total + current;
};

// "Give your answer in words" answered "Ninety-two thousand" for "ninety-two thousand five hundred" —
// a prefix of the mark scheme, which every containment tier reads as agreeing with it.
export const submittedMissesNumberWordsScheme = (submitted, correct) => {
  const c = numberWordsValue(correct);
  if (c === null) return false;
  const s = numberWordsValue(submitted);
  return s !== null && s !== c;
};

// A place/person/title mark scheme ("Dunton Green", "Top of the Pops") answered with only part of it
// ("Dunton", "Top"). Content words only — lowercase joiners ("of", "the") are neither required nor
// counted. Mirrors ExplanatoryAnswerMatcher::submittedDropsProperNounWord.
const NAME_JOINER_WORDS = new Set(["of", "the", "a", "an", "and", "in", "on", "at", "to", "for", "de", "la", "von"]);

const properNounContentWords = (value) => {
  const words = plainAnswerText(value).replace(/ /g, " ").replace(/\s+/g, " ").trim().split(" ").filter(Boolean);
  if (words.length < 2 || words.length > 6) return [];
  const content = [];
  for (const word of words) {
    if (NAME_JOINER_WORDS.has(word.toLowerCase())) continue;
    if (!/^\p{Lu}[\p{L}'’-]*$/u.test(word)) return [];
    content.push(word);
  }
  return content;
};

export const submittedDropsProperNounWord = (submitted, correct) => {
  const cContent = properNounContentWords(correct);
  if (cContent.length < 2 || cContent.length > 4) return false;
  const head = cContent[cContent.length - 1].toLowerCase();
  if (["triangle", "quadrilateral", "number", "numbers", "angle", "angles", "shape", "shapes",
    "numeral", "digit", "line", "factor", "multiple", "fraction", "decimal", "value", "unit",
    "figure", "sequence", "pattern", "sum", "product"].includes(head)) return false;
  const sWords = plainAnswerText(submitted).replace(/ /g, " ").replace(/\s+/g, " ").trim()
    .split(" ").filter(Boolean)
    .map((w) => w.toLowerCase())
    .filter((w) => !NAME_JOINER_WORDS.has(w));
  if (sWords.length === 0) return false;
  const cLower = cContent.map((w) => w.toLowerCase());
  return sWords.length < cLower.length && sWords.every((w) => cLower.includes(w));
};

const SHAPE_FAMILY_HEAD_NOUNS = ["triangle", "quadrilateral", "trapezium", "trapezoid",
  "parallelogram", "rhombus", "rectangle", "hexagon", "pentagon", "kite"];
const SHAPE_HEAD_ALIASES = { trapezium: ["trapezium", "trapezoid"], trapezoid: ["trapezium", "trapezoid"] };

const shapeWords = (text) => plainAnswerText(text)
  .replace(/[^\p{L}\p{N}]+/gu, " ")
  .trim()
  .toLowerCase()
  .split(" ")
  .filter(Boolean)
  .map((w) => (/[^s]s$/.test(w) ? w.slice(0, -1) : w));

// The mark scheme names a specialized member of a shape family ("Isosceles trapezium") and the
// submission is only the family ("trapezium"). Never a full match — the server scores it partial, or
// zero when the stem named the family itself. Mirrors ExplanatoryAnswerMatcher.
export const isGeneralShapeForSpecializedScheme = (submitted, correct) => {
  const cWords = shapeWords(correct);
  if (cWords.length < 2 || cWords.length > 4) return false;
  if (cWords.some((w) => w.length < 3)) return false;
  const head = cWords[cWords.length - 1];
  if (!SHAPE_FAMILY_HEAD_NOUNS.includes(head)) return false;
  const sWords = shapeWords(submitted);
  if (sWords.length !== 1) return false;
  return (SHAPE_HEAD_ALIASES[head] || [head]).includes(sWords[0]);
};

// A mark scheme offering alternative TUPLES of values — "12,8 or 12,4 or 4,8" for "write down a pair of
// possible numbers". Each alternative is a fixed-size list, so the answer is exactly one of them: how many
// values ("a pair" is two) is part of the answer, and which values go together is too. Null for every
// other shape of mark scheme, which other tiers own.
// Mirrors FuzzyAnswerMatching::correctIsValueTupleChoice.
export const correctIsValueTupleChoice = (correct) => {
  const alternatives = extractCorrectAlternatives(plainAnswerText(correct));
  if (alternatives.length < 2) return null;
  const tuples = [];
  for (const alt of alternatives) {
    const values = String(alt).split(",").map((v) => v.trim()).filter(Boolean);
    if (values.length < 2 || values.some((v) => !/^-?\d+(?:\.\d+)?$/.test(v))) return null;
    tuples.push(values.map((v) => String(parseFloat(v))));
  }
  // The alternatives must agree on size — "a pair" is two, whichever pair you pick.
  if (new Set(tuples.map((t) => t.length)).size !== 1) return null;
  return tuples;
};

// True when the submission is not exactly one of the offered tuples, order within it being free.
// Mirrors FuzzyAnswerMatching::submittedMissesValueTupleChoice.
export const submittedMissesValueTupleChoice = (submitted, correct) => {
  const tuples = correctIsValueTupleChoice(correct);
  if (tuples === null) return false;
  const given = plainAnswerText(submitted).split(/[,\s]+/).map((v) => v.trim()).filter(Boolean);
  // Only value lists are judged here; anything else is left to the other tiers.
  if (!given.length || given.some((v) => !/^-?\d+(?:\.\d+)?$/.test(v))) return false;
  const canon = (a) => a.map((v) => String(parseFloat(v))).sort().join("|");
  const g = canon(given);
  return !tuples.some((t) => canon(t) === g);
};

// Wrong on notation/completeness grounds alone, whatever it means. The lenient tiers (prose
// containment, the backend's AI graders) read straight past exactly these, so the client's
// upgrade-the-backend fallbacks check it before accepting an answer.
// A single-letter mark scheme ("G", "A or B") — every alternative is exactly one letter.
// Mirrors FuzzyAnswerMatching::correctIsSingleLetterAnswer.
export const correctIsSingleLetterAnswer = (correct) => {
  const plain = decodeHtmlSpaces(String(correct ?? "")).replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim();
  if (!plain) return null;
  const alts = extractCorrectAlternatives(plain);
  if (!alts.length) return null;
  const letters = [];
  for (const alt of alts) {
    const t = String(alt ?? "").replace(/\s+/g, "").trim();
    if (!t || !/^\p{L}$/u.test(t)) return null;
    letters.push(t.toUpperCase());
  }
  return [...new Set(letters)];
};

// Extra characters alongside the right letter ("gm" for "G") are wrong — AI Vision reads the letter
// inside and calls it correct. Mirrors FuzzyAnswerMatching::submittedMissesSingleLetterAnswer.
export const submittedMissesSingleLetterAnswer = (submitted, correct) => {
  const letters = correctIsSingleLetterAnswer(correct);
  if (!letters) return false;
  // "spinner A" / "shape B" name the label and say what kind of thing it is — the letter still IS the
  // answer. Only a plain noun may sit beside it: "gm" (no separate label token) and "not A" stay wrong.
  const tokens = decodeHtmlSpaces(String(submitted ?? ""))
    .replace(/<[^>]*>/g, " ")
    .replace(/[.,;:!?]/g, " ")
    .toLowerCase()
    .split(/\s+/)
    .filter((t) => t && t !== "the");
  if (tokens.length > 1 && tokens.length <= 3) {
    const labelTokens = tokens.filter((t) => /^\p{L}$/u.test(t));
    const nouns = tokens.filter((t) => /^\p{L}{2,}$/u.test(t));
    const negated = nouns.some((n) => ["not", "no", "never", "except", "without", "or", "and"].includes(n));
    if (labelTokens.length === 1 && nouns.length === tokens.length - 1 && !negated) {
      return !letters.includes(labelTokens[0].toUpperCase());
    }
  }
  const s = decodeHtmlSpaces(String(submitted ?? "")).replace(/<[^>]*>/g, " ").replace(/\s+/g, "").trim();
  if (!s) return false;
  if (!/^\p{L}$/u.test(s)) return true;
  return !letters.includes(s.toUpperCase());
};

// A tally-and-bracket code ("I I(I) I(I)", typed with pipes) folded to a comparable structure — tally
// glyphs unified, spacing dropped, brackets kept. Null when the text is not such a code.
// Mirrors FuzzyAnswerMatching::bracketCodeCanonical.
const bracketCodeCanonical = (value) => {
  const t = decodeHtmlSpaces(String(value ?? ""))
    .replace(/<[^>]*>/g, "")
    .replace(/[（]/g, "(")
    .replace(/[）]/g, ")")
    .replace(/\s+/g, "")
    .replace(/[Iil|1ǀ¦丨]/gu, "I");
  if (!t) return null;
  return /^[I()]+$/.test(t) ? t : null;
};

export const correctIsBracketCode = (correct) => {
  const c = bracketCodeCanonical(correct);
  return !!c && c.includes("(") && (c.match(/I/g) || []).length >= 2;
};

export const bracketCodeMatches = (submitted, correct) => {
  const s = bracketCodeCanonical(submitted);
  const c = bracketCodeCanonical(correct);
  return !!s && !!c && s === c;
};

// The question demands a particular WRITTEN form for a fraction answer — "in its simplest form", "as a
// mixed number". Only then is a value-equal alternative form a wrong answer.
// Mirrors FuzzyAnswerMatching::questionDemandsMixedNumberForm.
export const questionDemandsMixedNumberForm = (questionText) => {
  const t = plainAnswerText(questionText).toLowerCase();
  return t !== "" && /\bsimplest\s+form\b|\blowest\s+terms\b|\bsimplif(?:y|ied|ying)\b|\bfully\s+reduced\b|\bmixed\s+numbers?\b|\bwhole\s+number\s+and\s+a\s+fraction\b/.test(t);
};

// A mixed-number mark scheme ("7 1/2") answered with a top-heavy fraction ("15/2").
// Two cases (mirrors FuzzyAnswerMatching::submittedIsImproperFractionForMixedScheme):
//   1. The question demands the form — any improper fraction is wrong.
//   2. The values DON'T agree ("42/5" for "4 2/5") — wrong regardless; the AI grader has been seen
//      to misread a glued "42/5" as the mixed form and upgrade a rule "incorrect".
// A value-equal improper fraction stays acceptable when the stem does not demand the form
// (HABS 2013 Q6). Directional either way: a mixed number against an improper mark scheme stays ok.
export const submittedIsImproperFractionForMixedScheme = (submitted, correct, questionText = "") => {
  const plain = (v) => normalizeVulgarFractions(normalizeLatexFraction(plainAnswerText(v))).replace(/\s+/g, " ").trim();
  const cm = plain(correct).match(/^(-?)(\d+)\s+(\d+)\s*\/\s*(\d+)$/);
  if (!cm) return false;
  const sm = plain(submitted).match(/^(-?)(\d+)\s*\/\s*(\d+)$/);
  if (!sm) return false;
  const den = parseInt(sm[3], 10);
  if (!(den > 0) || parseInt(sm[2], 10) < den) return false;
  if (questionDemandsMixedNumberForm(questionText)) return true;
  const cSign = cm[1] === "-" ? -1 : 1;
  const sSign = sm[1] === "-" ? -1 : 1;
  const cVal = cSign * (parseInt(cm[2], 10) + parseInt(cm[3], 10) / parseInt(cm[4], 10));
  const sVal = sSign * (parseInt(sm[2], 10) / den);
  return Math.abs(cVal - sVal) > 1e-9;
};

// A bare month mark scheme ("November") — the question asked WHICH MONTH, not which date, so a day
// number the answer never claimed ("November 1") is a different answer. AI Vision reads the right
// month and marks it correct. Mirrors FuzzyAnswerMatching::submittedAddsDayNumberToMonthScheme.
const CALENDAR_MONTH_NAMES = [
  "january", "february", "march", "april", "may", "june", "july",
  "august", "september", "october", "november", "december",
  "jan", "feb", "mar", "apr", "jun", "jul", "aug", "sep", "sept", "oct", "nov", "dec",
];

export const submittedAddsDayNumberToMonthScheme = (submitted, correct) => {
  const c = plainAnswerText(correct).toLowerCase().replace(/[.\s]+$/, "");
  if (!CALENDAR_MONTH_NAMES.includes(c)) return false;
  const s = plainAnswerText(submitted).toLowerCase();
  return new RegExp(`\\b${c}`).test(s) && /\d/.test(s);
};

// Mirrors FuzzyAnswerMatching::extractCoordinatePairs.
const COORD_PAIR_RE = /\(\s*(-?\d+(?:\.\d+)?)\s*,\s*(-?\d+(?:\.\d+)?)\s*\)/g;

export const extractCoordinatePairs = (text) => {
  const t = decodeHtmlEntities(decodeHtmlSpaces(String(text ?? "")).replace(/<[^>]*>/g, ""));
  const pairs = [];
  for (const m of t.matchAll(COORD_PAIR_RE)) pairs.push([parseFloat(m[1]), parseFloat(m[2])]);
  if (pairs.length === 0) {
    const bare = t.trim().match(/^(-?\d+(?:\.\d+)?)\s*,\s*(-?\d+(?:\.\d+)?)$/);
    if (bare) pairs.push([parseFloat(bare[1]), parseFloat(bare[2])]);
  }
  return pairs;
};

// The mark scheme is nothing but coordinate pairs — "(3, 3)", "(3, 3), (4, 4)". The brackets and the
// space after the comma are notation, so "3,3" is the same point written plainly, but the ORDER of the
// two numbers is the answer. Mirrors FuzzyAnswerMatching::correctIsCoordinatePair.
export const correctIsCoordinatePair = (correct) => {
  const plain = decodeHtmlEntities(decodeHtmlSpaces(String(correct ?? "")).replace(/<[^>]*>/g, "")).trim();
  if (!plain || extractCoordinatePairs(plain).length === 0) return false;
  const rest = plain.replace(/\(\s*-?\d+(?:\.\d+)?\s*,\s*-?\d+(?:\.\d+)?\s*\)/g, "");
  return rest.replace(/\band\b|[,;]/gi, " ").trim() === "";
};

// Mirrors FuzzyAnswerMatching::coordinateAnswerMatches — positional first, then as a set.
const coordinatePairsEqual = (a, b) => Math.abs(a[0] - b[0]) < 1e-4 && Math.abs(a[1] - b[1]) < 1e-4;

export const coordinateAnswerMatches = (submitted, correct) => {
  const subPairs = extractCoordinatePairs(submitted);
  const corPairs = extractCoordinatePairs(correct);
  if (subPairs.length === 0 || corPairs.length === 0) return false;
  if (subPairs.length !== corPairs.length) return false;
  if (corPairs.every((cp, i) => coordinatePairsEqual(subPairs[i], cp))) return true;
  // Same set of points, different typing order — a coordinate LIST is a set ("find all the pairs of
  // whole numbers that p and q could be"), unless the question asks for a route/sequence, which is
  // graded by the route/label-sequence tier and never reaches here. Match each submitted pair against
  // an unused correct pair so a student who typed the same pairs in another order isn't marked wrong.
  // The order of the two numbers WITHIN a pair still decides it — "(21, 22)" is not "(22, 21)".
  const remaining = [...corPairs];
  return subPairs.every((sp) => {
    const i = remaining.findIndex((cp) => coordinatePairsEqual(sp, cp));
    if (i < 0) return false;
    remaining.splice(i, 1);
    return true;
  });
};

// Mirrors FuzzyAnswerMatching::submissionIsDeterministicallyWrong.
export const submissionIsDeterministicallyWrong = (submitted, correct, questionText = "") => {
  if (String(submitted ?? "").trim() === "") return false;
  submitted = canonicalizeDegreeGlyphs(submitted);
  correct = canonicalizeDegreeGlyphs(correct);
  // A missing or wrong-dimension unit ("124" / "124 cm²" for "124 m²") is wrong on the quantity, so the
  // AI must not re-accept it on the digits — same rule isAnswerCorrect applies.
  if (measuredUnitVerdict(submitted, correct, questionText) === false) return true;
  // A coordinate mark scheme is positional — "(22, 21)" is not "(21, 22)". Every value-set tier below
  // (and the AI) compares the two as the same pair of numbers.
  if (correctIsCoordinatePair(correct) && extractCoordinatePairs(submitted).length > 0
    && !coordinateAnswerMatches(submitted, correct)) return true;
  const namedPair = correctIsNamedPair(correct);
  if (namedPair && !namedPairMatches(submitted, namedPair)) return true;
  // "=/" is a ≠, not an "=" with a stray slash — but that is exactly how the AI reads it.
  const expectedOperator = canonicalComparisonOperator(correct);
  if (expectedOperator && canonicalComparisonOperator(submitted) !== expectedOperator) return true;
  // A power mark scheme with a dropped/wrong power (e.g. "1+2³+…" for "1³+2³+…") must not be
  // upgraded by the prose / value-equivalence client fallbacks.
  if (isNumericPowerExpression(correct) && !matchesNumericPower(submitted, correct)) return true;
  // Brackets dropped from a bracketed mark scheme — same symbols, different sum.
  if (bracketedCalculationMismatch(submitted, correct)) return true;
  // A sum with a dropped "+" — the AI / prose tiers read the same digits and call it correct.
  if (isSumExpression(correct) && !(isSumExpression(submitted) && sumExpressionCanonical(submitted) === sumExpressionCanonical(correct))) return true;
  // Truncated edge route / wrong coin list (£ vs p).
  if (correctIsEdgeRoute(correct) && !edgeRouteMatches(submitted, correct)) return true;
  if (isMoneyDenominationList(correct) && !moneyDenominationListMatches(submitted, correct)) return true;
  // Unfinished equation ending at "=".
  if (/=\s*$/u.test(String(submitted ?? "").trim()) && !/=\s*$/u.test(String(correct ?? "").trim())) return true;
  // "Write down A PAIR of numbers" answered with three ("12,4,8") — every prose/AI tier finds the required
  // pair among the values and reads straight past the extra one.
  if (submittedMissesValueTupleChoice(submitted, correct)) return true;
  // Single-letter answers ("G") — "gm" contains the letter but is not the answer.
  if (submittedMissesSingleLetterAnswer(submitted, correct)) return true;
  // A tally/bracket code ("I I(I) I(I)") — the bracket structure is the answer, so a code with one
  // dropped ("||||(|)") is wrong however many marks it shares.
  if (correctIsBracketCode(correct) && !bracketCodeMatches(submitted, correct)) return true;
  // "15/2" for "7 1/2" and "November 1" for "November" — the right value / the right month written in
  // a form the mark scheme rules out. Every value-equality tier above (and AI Vision) accepts both.
  if (submittedIsImproperFractionForMixedScheme(submitted, correct, questionText)) return true;
  if (submittedAddsDayNumberToMonthScheme(submitted, correct)) return true;
  // "800" off a timetable printing "0800" — the four digits are how the answer is written, but the AI
  // graders read the value ("800 is equivalent to 0800") and mark it correct.
  if (timetableDigitsMissing(submitted, correct, questionAsksForClockTime(questionText))) return true;
  if (yesNoVerdictConflict(submitted, correct)) return true;
  return hasStrayBackslash(submitted)
    // "5 04" for "£5.04" — a space typed where the decimal point belongs.
    || submittedSpacesDecimalPoint(submitted, correct)
    // "0,918" for 0.918 — nor is the comma. Also "10,274" for "10.274".
    || submittedCommaDecimalsNumber(submitted, correct)
    // A discrete value set ("1, 2, 7, 14", "the primes to 20") answered with a repeat or an extra value
    // is a different, wrong claim — the prose/AI tiers read the required values as present and agree.
    || (correctIsShortValueSet(correct) && !answerSetMatchesExactly(submitted, correct))
    || rotationDirectionConflict(submitted, correct)
    || likelihoodLabelConflict(submitted, correct)
    || submittedMissesRangeEndpoints(submitted, correct)
    || submittedIsDanglingFragment(submitted, correct)
    // "Ninety-two thousand" for "ninety-two thousand five hundred", "Dunton" for "Dunton Green" — the
    // right answer with a piece missing, which every containment tier (and the AI) reads as agreeing.
    || submittedMissesNumberWordsScheme(submitted, correct)
    || submittedDropsProperNounWord(submitted, correct)
    // An incomplete "list all the ways" answer is a run of lines lifted verbatim from the mark scheme,
    // so isShortValueInProseAnswer finds every one of them and upgrades the backend's correct "partial".
    || (correctIsSumEnumeration(correct) && !sumEnumerationMatches(submitted, correct));
};

const gcdOf = (a, b) => (b === 0 ? a : gcdOf(b, a % b));

// A fraction (or the fractional part of a mixed number) already in lowest terms. A value that isn't a
// fraction at all (whole number, decimal, prose) isn't "unsimplified" — returns true so other tiers own
// it. Mirrors FuzzyAnswerMatching::fractionIsInSimplestForm on the backend.
export const fractionIsInSimplestForm = (value) => {
  const v = normalizeLatexFraction(decodeHtmlSpaces(String(value ?? "")).replace(/<[^>]*>/g, ""))
    .trim()
    .toLowerCase();
  let n;
  let d;
  let m = v.match(/^-?\d+\s+(\d+)\s*\/\s*(\d+)$/);
  if (m) {
    n = parseInt(m[1], 10);
    d = parseInt(m[2], 10);
  } else if ((m = v.match(/^(-?\d+)\s*\/\s*(\d+)$/))) {
    n = Math.abs(parseInt(m[1], 10));
    d = parseInt(m[2], 10);
  } else {
    return true;
  }
  if (!d || !n) return true;
  return gcdOf(n, d) === 1;
};

// A question whose answer is an ordered sequence — order matters, so a correct set of values in
// the wrong order must not be accepted (e.g. "list these words in order, starting with the lowest").
export const requiresOrderedAnswer = (question) => {
  if (!question) return false;
  const type = String(question.type || question.question_type || "").toLowerCase();
  if (type === "ordering") return true;
  const text = decodeHtmlSpaces(String(question.question_text || question.text || question.question || ""))
    .replace(/<[^>]*>/g, " ")
    .replace(/\s+/g, " ")
    .toLowerCase();
  if (!text) return false;
  return /\bin\s+(?:ascending|descending|increasing|decreasing|numerical|alphabetical|reverse)?\s*order\b/.test(text)
    || /\border\s+of\b/.test(text)
    || /\b(?:arrange|rearrange|list|order|put|rank|sort|reorder)\b[^.]*\border\b/.test(text)
    || /\bstarting\s+with\s+the\s+(?:lowest|highest|smallest|largest|biggest|first)\b/.test(text)
    || /\bfrom\s+(?:lowest|highest|smallest|largest|biggest)\s+to\b/.test(text);
};

// A mark scheme that is a SEQUENCE rather than an unordered set: every part is numeric and some value
// repeats (e.g. the digital-root pattern "1, 4, 9, 7, 7, 9, 4, 1"). Order carries meaning, so the same
// multiset in a different order is a different answer. A set of DISTINCT values (factors "1, 2, 7, 14")
// stays order-insensitive. Mirrors FuzzyAnswerMatching::correctIsNumericSequence on the backend.
export const correctIsNumericSequence = (correct) => {
  const parts = splitMultiValueAnswer(String(correct ?? ""));
  if (parts.length < 3) return false;
  if (!parts.every((p) => /^-?\d+(?:\.\d+)?$/.test(String(p).trim()))) return false;
  const normalized = parts.map((p) => String(parseFloat(String(p).trim())));
  return new Set(normalized).size < normalized.length;
};

// True when the submitted values ARE the expected values, just sequenced differently — the only case
// that may be reported as "wrong order". A list with a wrong/missing value is plainly incorrect, and
// saying "wrong order" about it (as the greedy in-order scan used to) misleads the student.
export const isSameValueMultiset = (submittedParts, correctParts) => {
  if (submittedParts.length !== correctParts.length) return false;
  const used = new Set();
  for (const cp of correctParts) {
    let hit = false;
    for (let si = 0; si < submittedParts.length; si++) {
      if (used.has(si)) continue;
      if (singleValueMatches(submittedParts[si], cp)) {
        used.add(si);
        hit = true;
        break;
      }
    }
    if (!hit) return false;
  }
  return true;
};

// A discrete value set — "1, 2, 7, 14" (all the factors), "2, 3, 5, 7, 11, 13, 17, 19" (the primes to
// 20). Every value is required and each is required ONCE, so the answer is decided by exact set
// equality. A repeating numeric sequence ("1, 4, 9, 7, 7, 9, 4, 1") is ordered data, not a set, and a
// scheme whose parts are phrases is prose. Mirrors FuzzyAnswerMatching::blankAcceptIsValueSet.
export const correctIsShortValueSet = (correct) => {
  const parts = splitMultiValueAnswer(String(correct ?? ""));
  if (parts.length < 2) return false;
  if (correctIsNumericSequence(correct)) return false;
  return parts.every((p) => String(p).trim().split(/\s+/).filter(Boolean).length <= 2);
};

// Set equality for the above: the student must give as many values as the scheme lists, each matching a
// distinct one. Surplus values — a repeat ("1,2,4,7,28,1,2,14") or a wrong extra ("…,14,28,3") — are
// what the old "every expected value was found somewhere" fallback let through as Correct while the
// backend scored 0. Mirrors FuzzyAnswerMatching::answerSetMatches, including its two readings of the
// submission (whitespace+comma, then comma only, so "66 °, 66 °" keeps its unit chips).
export const answerSetMatchesExactly = (submitted, correct) => {
  const tokenize = (v, strict) => {
    let s = expandMultiValueAndJoiners(decodeHtmlSpaces(String(v ?? "")).replace(/<[^>]*>/g, " "));
    // "or"/"and" are separators on the submitted side: a two-value set ("Birmingham, London") is
    // naturally answered "Birmingham and London", and the whitespace tokenizer would otherwise read the
    // joiner as a third value and fail the right answer.
    if (strict) s = s.replace(/\s+(?:or|and)\s+/gi, ", ");
    return s.trim().split(strict ? /[\s,]+/ : /[\s,;|]+/).map((p) => p.trim()).filter(Boolean);
  };
  const expectedParts = tokenize(correct, false);
  if (expectedParts.length < 2) return false;

  const readings = [tokenize(submitted, true)];
  if (String(submitted ?? "").includes(",")) {
    readings.push(String(submitted).split(",").map((p) => p.trim()).filter(Boolean));
  }

  return readings.some((submittedParts) => {
    if (submittedParts.length !== expectedParts.length) return false;
    const used = new Set();
    return expectedParts.every((exp) => {
      for (let i = 0; i < submittedParts.length; i++) {
        if (used.has(i)) continue;
        if (singleValueMatches(submittedParts[i], exp)) {
          used.add(i);
          return true;
        }
      }
      return false;
    });
  });
};

// "List the prime numbers from 0 to 20" is all-or-nothing on the server: one duplicate or one missing
// value hides the seven the student got right. Returns { matched, total, extra, ratio } when the answer
// earns partial credit, otherwise null — never a verdict on its own, callers only soften Incorrect →
// Partial with it. Only bare-number schemes qualify; prose ("3, because it is odd") splits on commas
// too and scoring that by value overlap is meaningless. Both readings of the submission are tried so a
// dropped separator ("17 19" for "17, 19") is still read as two values.
export const numericListPartialCredit = (submitted, correct) => {
  const text = decodeHtmlEntities(String(submitted ?? "").replace(/&nbsp;/gi, " ").replace(/<[^>]*>/g, " "))
    .replace(/÷/g, "/").replace(/×/g, "*");
  if (!text.trim() || !String(correct ?? "").trim()) return null;
  const isBareNumber = (p) => /^-?\d+(?:\.\d+)?$/.test(String(p).trim());
  const correctParts = splitMultiValueAnswer(String(correct));
  if (correctParts.length < 2 || !correctParts.every(isBareNumber)) return null;

  const readings = [
    splitMultiValueAnswer(text),
    text.trim().split(/[\s,]+/).map((p) => p.trim()).filter(Boolean),
  ].filter((r) => r.length > 0 && r.every(isBareNumber));
  if (!readings.length) return null;

  let best = null;
  readings.forEach((subParts) => {
    const remaining = [...subParts];
    let matched = 0;
    correctParts.forEach((cp) => {
      const i = remaining.findIndex((sp) => singleValueMatches(sp, cp));
      if (i >= 0) {
        remaining.splice(i, 1);
        matched++;
      }
    });
    const score = matched - remaining.length;
    if (!best || score > best.score) best = { matched, extra: remaining.length, score };
  });

  if (!best || best.matched === 0) return null;
  if (best.matched === correctParts.length && best.extra === 0) return null;
  return {
    matched: best.matched,
    total: correctParts.length,
    extra: best.extra,
    ratio: Math.max(0, best.score) / correctParts.length,
  };
};

export const isAnswerCorrect = (submitted, correct, options = {}) => {
  if (submitted == null || submitted === "" || submitted === "—") return false;
  if (correct == null || correct === "" || correct === "—") return false;
  submitted = canonicalizeDegreeGlyphs(submitted);
  correct = canonicalizeDegreeGlyphs(correct);
  if (yesNoVerdictConflict(submitted, correct)) return false;
  // Mirrors FuzzyAnswerMatching: an area/volume mark scheme is a quantity in a fixed dimension, and a
  // "state the units of your answer" stem makes naming that unit half the task. Both are decided here
  // so Check answer can't upgrade what the server (and the Review page) call wrong.
  {
    const unitVerdict = measuredUnitVerdict(submitted, correct, options.questionText);
    if (unitVerdict !== null) return unitVerdict;
  }
  // "4 or 5" on a stem asking for ONE number: naming both options is a hedge, not a choice. Hoisted to
  // the top because the alternatives reach the tiers below in several shapes (the raw "4 or 5", the
  // display list "4, 5" compared as a value set, the per-value list tier) and each accepted it.
  if (questionAsksForOneValue(options.questionText)
      && submissionHedgesAlternatives(submitted, extractCorrectAlternatives(plainAnswerText(correct)))) {
    return false;
  }
  if (algebraicEquationMatches(submitted, correct)) return true;
  // Compact equation quoted inside prose ("180+180=360" in "…180° + 180° = 360°").
  {
    const stripDeg = (v) => String(v ?? "").replace(/\\circ\b|°/g, "").replace(/[×⋅·]/g, "*").replace(/÷/g, "/")
      .replace(/[−–—]/g, "-").replace(/<[^>]*>/g, " ");
    const compact = (v) => stripDeg(v).replace(/[^\d+\-*/^=()]/g, "");
    const s = compact(submitted);
    const c = compact(correct);
    if (s.length >= 5 && /[+\-*/=]/.test(s) && c.includes(s)) return true;
  }
  // A coordinate is an ORDERED pair: "22,21" is not "(21, 22)". Decided before every tier below —
  // the multiset fallback splits both on the comma and reads them as the same two values. Only when
  // the submission is itself readable as points; anything else falls through to the normal tiers.
  if (correctIsCoordinatePair(correct) && extractCoordinatePairs(submitted).length > 0) {
    return coordinateAnswerMatches(submitted, correct);
  }
  // "(552 + 448) × (552 - 448)" answered without its brackets is a different sum written with the same
  // symbols — decided before the tiers that compare an answer by the digits and operators it contains.
  if (bracketedCalculationMismatch(submitted, correct)) return false;
  // "List ALL the ways to make 60p" — every sum in the mark scheme is required, so decide the whole
  // list by set equality before the tiers below, which credit a subset of it as a full match.
  if (correctIsSumEnumeration(correct)) return sumEnumerationMatches(submitted, correct);
  // A route / order of labelled points ("A E D B C G") — the whole sequence is the answer.
  if (correctIsLabelSequence(correct)) return labelSequenceMatches(submitted, correct);
  if (correctIsEdgeRoute(correct)) return edgeRouteMatches(submitted, correct);
  // A sum ("1+3+5+…", "20p + 20p + 20p") — the operators are part of the answer. Ahead of the coin
  // list below, the order isFuzzyTextMatch decides them in: a sum of money terms is also a money
  // list, and the multiset compare there would accept it with the "+" signs dropped.
  if (isSumExpression(correct)) {
    return isSumExpression(submitted) && sumExpressionCanonical(submitted) === sumExpressionCanonical(correct);
  }
  // A coin list ("£1, 20p, 5p, 1p") — decided here, like isFuzzyTextMatch does, because the
  // multi-value fallback below splits it on the commas and compares "1" with "£1" one token at a
  // time, where a bare number passes for a pounds amount.
  if (isMoneyDenominationList(correct)) return moneyDenominationListMatches(submitted, correct);
  // "Which TWO cities…" — both names are the answer, in either order.
  const namedPair = correctIsNamedPair(correct);
  if (namedPair) return namedPairMatches(submitted, namedPair);
  if (options.ordered || correctIsNumericSequence(correct)) {
    const correctOrdered = splitMultiValueAnswer(String(correct));
    if (correctOrdered.length > 1) {
      let submittedOrdered = splitMultiValueAnswer(String(submitted));
      // When the correct sequence is all numbers, students often separate them with spaces
      // ("798 879 897") or bare commas ("798,879,897"), which splitMultiValueAnswer doesn't break.
      // Re-tokenize on any whitespace/comma run so a correct space-separated list isn't failed.
      const correctAllNumeric = correctOrdered.every((p) => /^-?\d+(?:\.\d+)?$/.test(String(p).trim()));
      if (correctAllNumeric && submittedOrdered.length < correctOrdered.length) {
        const reSplit = String(submitted).trim().split(/[\s,]+/).map((p) => p.trim()).filter(Boolean);
        if (reSplit.length > submittedOrdered.length) submittedOrdered = reSplit;
      }
      if (submittedOrdered.length !== correctOrdered.length) return false;
      return correctOrdered.every((cp, i) => singleValueMatches(submittedOrdered[i], cp));
    }
  }
  if (singleValueMatches(submitted, correct, options.questionText)) return true;
  if (estimateNumericAnswerEquivalent(submitted, correct, options.questionText)) return true;
  if (countQuestionNumericAnswerEquivalent(submitted, correct, options.questionText)) return true;
  // A discrete value set is exact: the fallback below only checks that every EXPECTED value was found
  // somewhere, so a list carrying the whole set plus a repeat ("1,2,4,7,28,1,2,14") or an extra wrong
  // value ("1,2,4,7,14,28,3") was upgraded to Correct over the backend's 0 — the client upgrade tier on
  // the take page reads this function. Decided by set equality instead, as the backend does.
  if (correctIsShortValueSet(correct)) return answerSetMatchesExactly(submitted, correct);
  const correctParts = splitMultiValueAnswer(String(correct));
  const submittedParts = splitMultiValueAnswer(String(submitted));
  if (correctParts.length > 1 && submittedParts.length > 1) {
    let matched = 0;
    const usedSubmitted = new Set();
    for (const cPart of correctParts) {
      for (let si = 0; si < submittedParts.length; si++) {
        if (usedSubmitted.has(si)) continue;
        if (singleValueMatches(submittedParts[si], cPart)) {
          matched++;
          usedSubmitted.add(si);
          break;
        }
      }
    }
    if (matched === correctParts.length) return true;
    // Submitting a subset of required values is partial, not correct — fall through.
  }
  return false;
};
